<template>
  <div class="budget-starter">
    <!-- 메인 컨테이너 -->
    <div class="starter-container">
      <!-- 좌측: 진행 상태 사이드바 -->
      <aside class="progress-sidebar">
        <!-- 연도 선택 영역 -->
        <div class="year-selector">
          <button 
            class="year-nav-btn" 
            @click="changeYear(-1)"
            :disabled="parseInt(searchParams.lebYear) <= minYear"
          >
            <v-icon size="20">mdi-chevron-left</v-icon>
          </button>
          <div class="year-display">
            <span class="year-value">{{ searchParams.lebYear }}</span>
            <span class="year-unit">년</span>
          </div>
          <button 
            class="year-nav-btn" 
            @click="changeYear(1)"
            :disabled="parseInt(searchParams.lebYear) >= maxYear"
          >
            <v-icon size="20">mdi-chevron-right</v-icon>
          </button>
        </div>

        <!-- 추경차수 표시 -->
        <div class="budget-class-badge" :class="budgetClassStyle">
          <v-icon size="14">{{ budgetClassIcon }}</v-icon>
          <span>{{ budgetClassLabel }}</span>
        </div>

        <div class="sidebar-header">
          <h2>예산 편성</h2>
          <p>질의응답 기반 자동 계산</p>
        </div>

        <!-- 진행 상태 -->
        <div class="progress-info">
          <div class="progress-circle">
            <svg viewBox="0 0 100 100">
              <circle class="progress-bg" cx="50" cy="50" r="45"/>
              <circle 
                class="progress-fill" 
                cx="50" cy="50" r="45"
                :style="{ strokeDashoffset: progressOffset }"
              />
            </svg>
            <span class="progress-percent">{{ Math.round(progressPercent) }}%</span>
          </div>
          <div class="progress-text">
            <span class="current">{{ currentStep }}</span>
            <span class="divider">/</span>
            <span class="total">{{ totalQuestions }}</span>
          </div>
        </div>

        <!-- 세입/세출 요약 -->
        <div class="summary-section">
          <div class="summary-item revenue">
            <div class="summary-header">
              <span class="dot"></span>
              <span class="label">세입</span>
            </div>
            <span class="value">{{ formatMoney(totalRevenue) }}원</span>
          </div>
          <div class="summary-item expense">
            <div class="summary-header">
              <span class="dot"></span>
              <span class="label">세출</span>
            </div>
            <span class="value">{{ formatMoney(totalExpense) }}원</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item balance" :class="{ negative: balance < 0 }">
            <div class="summary-header">
              <v-icon size="16" :color="balance >= 0 ? '#10b981' : '#ef4444'">
                {{ balance >= 0 ? 'mdi-check-circle' : 'mdi-alert-circle' }}
              </v-icon>
              <span class="label">잔액</span>
            </div>
            <span class="value">{{ formatMoney(balance) }}원</span>
          </div>
        </div>

        <!-- 인건비 지출 비율 -->
        <div class="labor-cost-section" v-if="totalExpense > 0">
          <div class="labor-cost-header">
            <v-icon size="18" color="#374151">mdi-account-cash</v-icon>
            <span>인건비 지출 비율</span>
          </div>
          <div class="labor-cost-content">
            <div class="labor-cost-item standard">
              <span class="labor-cost-label">기준비율</span>
              <span class="labor-cost-value">{{ standardLaborCostRate }}%</span>
            </div>
            <v-tooltip location="bottom" max-width="320">
              <template v-slot:activator="{ props }">
                <div 
                  v-bind="props"
                  class="labor-cost-item current" 
                  :class="{ warning: isLaborCostBelowStandard, safe: !isLaborCostBelowStandard }"
                >
                  <span class="labor-cost-label">현재비율</span>
                  <span class="labor-cost-value">{{ currentLaborCostRate.toFixed(1) }}%</span>
                  <v-icon size="12" class="tooltip-icon">mdi-help-circle-outline</v-icon>
                </div>
              </template>
              <div class="labor-tooltip-content">
                <div class="tooltip-title">산출근거</div>
                <div class="tooltip-row">
                  <span class="tooltip-label">인건비 지출</span>
                  <span class="tooltip-value">{{ formatMoney(totalLaborCost) }}원</span>
                </div>
                <div class="tooltip-note">(요양보호사·사회복지사 급여+보험+퇴직금)</div>
                <div class="tooltip-row">
                  <span class="tooltip-label">수입</span>
                  <span class="tooltip-value">{{ formatMoney(laborCostBaseRevenue) }}원</span>
                </div>
                <div class="tooltip-note">(장기요양급여+본인부담금+가산금)</div>
                <div class="tooltip-formula">
                  비율 = {{ formatMoney(totalLaborCost) }} ÷ {{ formatMoney(laborCostBaseRevenue) }} × 100 = {{ currentLaborCostRate.toFixed(1) }}%
                </div>
              </div>
            </v-tooltip>
          </div>
          <div class="labor-cost-status" :class="{ warning: isLaborCostBelowStandard }">
            <v-icon size="14" :color="isLaborCostBelowStandard ? '#f59e0b' : '#10b981'">
              {{ isLaborCostBelowStandard ? 'mdi-alert' : 'mdi-check-circle' }}
            </v-icon>
            <span v-if="isLaborCostBelowStandard">기준비율 미달</span>
            <span v-else>기준비율 충족</span>
          </div>
        </div>

        <!-- 섹션 네비게이션 -->
        <nav class="section-nav">
          <button 
            class="nav-item" 
            :class="{ active: currentSection === 'revenue', completed: isRevenueCompleted }"
            @click="goToSection('revenue')"
          >
            <span class="nav-icon revenue">
              <v-icon size="18">mdi-arrow-down-bold</v-icon>
            </span>
            <span class="nav-text">세입 항목</span>
            <span class="nav-count">{{ revenueAnsweredCount }}/{{ revenueQuestions.length }}</span>
          </button>
          <button 
            class="nav-item" 
            :class="{ active: currentSection === 'expense', completed: isExpenseCompleted }"
            @click="goToSection('expense')"
          >
            <span class="nav-icon expense">
              <v-icon size="18">mdi-arrow-up-bold</v-icon>
            </span>
            <span class="nav-text">세출 항목</span>
            <span class="nav-count">{{ expenseAnsweredCount }}/{{ expenseQuestions.length }}</span>
          </button>
        </nav>
      </aside>

      <!-- 우측: 질문 영역 -->
      <main class="question-main">
        <!-- 질문이 없는 경우 빈 상태 표시 -->
        <div v-if="totalQuestions === 0" class="empty-state">
          <div class="empty-icon">
            <v-icon size="64" color="#9ca3af">mdi-clipboard-text-outline</v-icon>
          </div>
          <h3>질문이 준비되지 않았습니다</h3>
          <p>세입/세출 질문을 추가해주세요.</p>
        </div>

        <!-- 질문이 있는 경우 -->
        <template v-else>
          <!-- 상단 네비게이션 -->
          <div class="top-nav">
            <div class="breadcrumb">
              <span class="section-badge" :class="currentSection">
                {{ currentQuestion?.section }}
              </span>
              <v-icon size="16" color="#9ca3af">mdi-chevron-right</v-icon>
              <span class="step-info">질문 {{ currentQuestion?.sequence }}</span>
            </div>
            <div class="step-dots">
              <span 
                v-for="(q, idx) in currentSectionQuestions" 
                :key="idx"
                class="dot"
                :class="{ 
                  active: q.code === currentQuestion?.code,
                  filled: answers[q.code] !== undefined && answers[q.code] !== '',
                  [currentSection]: true
                }"
                @click="goToQuestion(q.code)"
              ></span>
            </div>
          </div>

          <!-- 질문 카드 -->
          <transition name="slide-fade" mode="out-in" @after-enter="focusFirstInput">
            <div class="question-card" :class="currentSection" :key="currentStep">
              <!-- 질문 헤더 -->
              <div class="question-header">
                <div class="question-number" :class="currentSection">Q{{ currentQuestion?.sequence }}</div>
              </div>

              <!-- 질문 내용 -->
              <h2 class="question-title">{{ currentQuestion?.question }}</h2>

              <!-- 힌트 -->
              <div class="hint-box" v-if="currentQuestion?.hint">
                <v-icon size="18" color="#7c3aed">mdi-lightbulb-on</v-icon>
                <span>{{ currentQuestion?.hint }}</span>
              </div>

              <!-- 설명 박스 -->
              <div class="description-box" :class="currentSection" v-if="currentQuestion?.description">
                <div class="desc-icon">
                  <v-icon size="20" color="#6b7280">mdi-information</v-icon>
                </div>
                <div class="desc-content">
                  <ul v-if="parsedDescription.isList" class="desc-list">
                    <li v-for="(item, idx) in parsedDescription.items" :key="idx">
                      <span class="desc-number">{{ item.number }}</span>
                      <span class="desc-text">{{ item.text }}</span>
                    </li>
                  </ul>
                  <p v-else>{{ currentQuestion?.description }}</p>
                </div>
              </div>

              <!-- 입력 영역 -->
              <div class="input-section">
                <!-- 숫자 입력 -->
                <template v-if="currentQuestion?.inputType === 'number'">
                  <div class="number-input-row">
                    <label class="field-label">{{ currentQuestion?.suffix === '명' ? '인원수' : '금액' }}</label>
                    <div class="input-wrapper" :class="{ focused: inputFocused, filled: currentAnswer, [currentSection]: true }">
                      <input
                        type="text"
                        class="main-input"
                        :placeholder="currentQuestion?.placeholder || '0'"
                        v-model="currentAnswer"
                        @input="handleNumberInput"
                        @focus="inputFocused = true"
                        @blur="inputFocused = false"
                        @keyup.enter="handleEnterKey"
                      />
                      <span class="input-suffix">{{ (currentQuestion?.suffix || '원') + (currentPeriodLabel ? ' ' + currentPeriodLabel : '') }}</span>
                    </div>
                  </div>
                </template>

                <!-- 선택 입력 -->
                <template v-else-if="currentQuestion?.inputType === 'select'">
                  <div class="select-input-row">
                    <label class="field-label">항목 선택</label>
                    <div class="select-wrapper" :class="currentSection">
                      <select v-model="currentAnswer" class="main-select">
                        <option value="">선택하세요</option>
                        <option 
                          v-for="option in currentQuestion?.options" 
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                      <v-icon class="select-arrow" size="20">mdi-chevron-down</v-icon>
                    </div>
                  </div>
                </template>

                <!-- 복합 입력 (등급 + 시간) -->
                <template v-else-if="currentQuestion?.inputType === 'composite'">
                  <div class="composite-grid">
                    <div class="composite-field" :class="currentSection" v-for="field in currentQuestion?.fields" :key="field.key">
                      <label class="field-label">{{ field.label }}</label>
                      <div class="select-wrapper">
                        <select v-model="compositeAnswers[field.key]" class="main-select">
                          <option value="">▼ 선택하세요</option>
                          <option 
                            v-for="opt in field.options" 
                            :key="opt.value"
                            :value="opt.value"
                          >
                            {{ opt.label }}
                          </option>
                        </select>
                        <v-icon class="select-arrow" size="20">mdi-chevron-down</v-icon>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- 다중 숫자 입력 -->
                <template v-else-if="currentQuestion?.inputType === 'multi-number'">
                  <div class="multi-number-grid">
                    <div 
                      class="multi-number-field" 
                      :class="currentSection" 
                      v-for="field in currentQuestion?.fields" 
                      :key="field.key"
                    >
                      <label class="field-label">{{ field.label }}</label>
                      <div class="input-wrapper" :class="{ filled: compositeAnswers[field.key], [currentSection]: true }">
                        <input
                          type="text"
                          class="main-input"
                          :placeholder="'0'"
                          :value="formatMultiNumberValue(field.key)"
                          @input="handleMultiNumberInput($event, field.key)"
                          @keyup.enter="handleEnterKey"
                        />
                        <span class="input-suffix" v-if="field.suffix">{{ field.suffix === '원' && getMultiNumberPeriodLabel ? '원 ' + getMultiNumberPeriodLabel : field.suffix }}</span>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- 급여 입력 (salary) -->
                <template v-else-if="currentQuestion?.inputType === 'salary'">
                  <div class="salary-input-section">
                    <!-- 급여 & 인원수 입력 -->
                    <div class="salary-inputs">
                      <div 
                        v-for="field in currentQuestion?.fields" 
                        :key="field.key"
                        class="salary-field"
                        :class="currentSection"
                      >
                        <label class="field-label">{{ field.label }}</label>
                        <div class="input-wrapper" :class="{ filled: compositeAnswers[field.key], [currentSection]: true }">
                          <input
                            type="text"
                            class="main-input"
                            :placeholder="'0'"
                            :value="formatMultiNumberValue(field.key)"
                            @input="handleMultiNumberInput($event, field.key)"
                            @keyup.enter="handleEnterKey"
                          />
                          <span class="input-suffix" v-if="field.suffix">{{ field.suffix === '원' ? '원 / 월' : field.suffix }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- 사회보험부담금 자동 계산 표시 -->
                    <div class="insurance-preview" v-if="salaryInsurancePreview">
                      <div class="insurance-header">
                        <v-icon size="18" color="#6b7280">mdi-shield-account</v-icon>
                        <span>사회보험부담금 (사업자 부담분)</span>
                        <span class="excluded-note" v-if="!currentQuestion?.insuranceConfig?.employmentInsurance">
                          (대표자: 고용·산재·퇴직금 제외)
                        </span>
                      </div>
                      <div class="insurance-summary">
                        <div class="summary-row">
                          <span class="summary-label">연간 급여</span>
                          <span class="summary-value">{{ formatMoney(salaryInsurancePreview.annualSalary) }}원</span>
                        </div>
                        <div class="summary-row">
                          <span class="summary-label">연간 사회보험부담금</span>
                          <span class="summary-value">{{ formatMoney(salaryInsurancePreview.annualInsurance - salaryInsurancePreview.retirement * 12) }}원</span>
                        </div>
                        <div class="summary-row" v-if="currentQuestion?.insuranceConfig?.retirement">
                          <span class="summary-label">연간 퇴직금</span>
                          <span class="summary-value">{{ formatMoney(salaryInsurancePreview.retirement * 12) }}원</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- 요양보호사 시급 입력 (caregiver-salary) - 대표자/일반 구분 -->
                <template v-else-if="currentQuestion?.inputType === 'caregiver-salary'">
                  <div class="caregiver-salary-section">
                    <!-- 대표자 여부 스위치 -->
                    <div class="ceo-toggle-row">
                      <div class="toggle-label">
                        <v-icon size="20" color="#6b7280">mdi-account-tie</v-icon>
                        <span>대표자(사업주)가 요양보호사로 근무합니까?</span>
                      </div>
                      <v-switch
                        v-model="compositeAnswers['hasCeo']"
                        color="primary"
                        hide-details
                        density="compact"
                        inset
                      ></v-switch>
                    </div>

                    <!-- 대표자 입력 영역 (스위치 ON일 때만) -->
                    <div class="ceo-inputs" v-if="compositeAnswers['hasCeo']">
                      <div class="input-group-header">
                        <v-icon size="16" color="#f59e0b">mdi-shield-account</v-icon>
                        <span>대표자 (고용·산재·퇴직금 제외)</span>
                      </div>
                      <div class="director-fields">
                        <div class="director-field expense">
                          <label class="field-label">대표자 수</label>
                          <div class="input-wrapper" :class="{ filled: compositeAnswers['ceoCount'], expense: true }">
                            <input
                              type="text"
                              class="main-input"
                              placeholder="1"
                              :value="formatMultiNumberValue('ceoCount')"
                              @input="handleMultiNumberInput($event, 'ceoCount')"
                              @keyup.enter="handleEnterKey"
                            />
                            <span class="input-suffix">명</span>
                          </div>
                        </div>
                        <div class="director-field expense">
                          <label class="field-label">대표자 시급</label>
                          <div class="input-wrapper" :class="{ filled: compositeAnswers['ceoHourlyWage'], expense: true }">
                            <input
                              type="text"
                              class="main-input"
                              placeholder="0"
                              :value="formatMultiNumberValue('ceoHourlyWage')"
                              @input="handleMultiNumberInput($event, 'ceoHourlyWage')"
                              @keyup.enter="handleEnterKey"
                            />
                            <span class="input-suffix">원 / 시간</span>
                          </div>
                        </div>
                      </div>
                      <!-- 대표자 월급 계산 -->
                      <div class="calc-note" v-if="Number(compositeAnswers['ceoHourlyWage']) > 0">
                        월급 = {{ formatMoney(Number(compositeAnswers['ceoHourlyWage']) * 60) }}원 ({{ formatMoney(Number(compositeAnswers['ceoHourlyWage'])) }}원 × 60시간)
                      </div>
                    </div>

                    <!-- 일반 요양보호사 입력 영역 -->
                    <div class="director-inputs">
                      <div class="input-group-header">
                        <v-icon size="16" color="#f59e0b">mdi-account-group</v-icon>
                        <span>일반 요양보호사 (모든 보험·퇴직금 적용)</span>
                      </div>
                      <div class="director-fields">
                        <div class="director-field expense">
                          <label class="field-label">요양보호사 수</label>
                          <div class="input-wrapper" :class="{ filled: compositeAnswers['caregiverCount'], expense: true, disabled: targetClientsCount <= 0 }">
                            <input
                              type="text"
                              class="main-input"
                              :placeholder="targetClientsCount > 0 ? String(targetClientsCount) : '수급자수 먼저 입력'"
                              :value="formatMultiNumberValue('caregiverCount')"
                              @input="handleMultiNumberInput($event, 'caregiverCount')"
                              @keyup.enter="handleEnterKey"
                              :disabled="targetClientsCount <= 0"
                            />
                            <span class="input-suffix">명</span>
                          </div>
                          <div class="field-hint" v-if="targetClientsCount > 0">기본값: 수급자 수 ({{ targetClientsCount }}명)</div>
                          <div class="field-hint warning" v-else>세입 - 목표 수급자수를 먼저 입력하세요</div>
                        </div>
                        <div class="director-field expense">
                          <label class="field-label">시급</label>
                          <div class="input-wrapper" :class="{ filled: compositeAnswers['caregiverHourlyWage'], expense: true }">
                            <input
                              type="text"
                              class="main-input"
                              placeholder="0"
                              :value="formatMultiNumberValue('caregiverHourlyWage')"
                              @input="handleMultiNumberInput($event, 'caregiverHourlyWage')"
                              @keyup.enter="handleEnterKey"
                            />
                            <span class="input-suffix">원 / 시간</span>
                          </div>
                        </div>
                      </div>
                      <!-- 일반 요양보호사 월급 계산 -->
                      <div class="calc-note" v-if="Number(compositeAnswers['caregiverHourlyWage']) > 0">
                        월급 = {{ formatMoney(Number(compositeAnswers['caregiverHourlyWage']) * 60) }}원 ({{ formatMoney(Number(compositeAnswers['caregiverHourlyWage'])) }}원 × 60시간)
                      </div>
                    </div>

                    <!-- 사회보험부담금 미리보기 -->
                    <div class="insurance-preview" v-if="caregiverInsurancePreview">
                      <div class="insurance-header">
                        <v-icon size="18" color="#6b7280">mdi-shield-account</v-icon>
                        <span>사회보험부담금 + 퇴직금 (사업자 부담분)</span>
                      </div>
                      <div class="insurance-summary">
                        <!-- 대표자 합계 -->
                        <template v-if="caregiverInsurancePreview.ceo">
                          <div class="summary-row">
                            <span class="summary-label">대표자 연간 급여</span>
                            <span class="summary-value">{{ formatMoney(caregiverInsurancePreview.ceo.annualSalary) }}원</span>
                          </div>
                          <div class="summary-row">
                            <span class="summary-label">대표자 연간 사회보험부담금</span>
                            <span class="summary-value">{{ formatMoney(caregiverInsurancePreview.ceo.annualInsurance) }}원</span>
                          </div>
                        </template>
                        <!-- 일반 요양보호사 합계 -->
                        <template v-if="caregiverInsurancePreview.caregiver">
                          <div class="summary-row">
                            <span class="summary-label">요양보호사 연간 급여</span>
                            <span class="summary-value">{{ formatMoney(caregiverInsurancePreview.caregiver.annualSalary) }}원</span>
                          </div>
                          <div class="summary-row">
                            <span class="summary-label">요양보호사 연간 사회보험+퇴직금</span>
                            <span class="summary-value">{{ formatMoney(caregiverInsurancePreview.caregiver.annualInsurance) }}원</span>
                          </div>
                        </template>
                        <!-- 총합계 -->
                        <div class="summary-row total">
                          <span class="summary-label">총 연간 비용</span>
                          <span class="summary-value highlight">{{ formatMoney(caregiverInsurancePreview.totalAnnual) }}원</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- 센터장 급여 입력 (director-salary) - 대표자/비대표자 구분 -->
                <template v-else-if="currentQuestion?.inputType === 'director-salary'">
                  <div class="director-salary-section">
                    <!-- 대표자 여부 스위치 -->
                    <div class="ceo-toggle-row">
                      <div class="toggle-label">
                        <v-icon size="20" color="#6b7280">mdi-account-tie</v-icon>
                        <span>대표자(사업주)가 센터장으로 근무합니까?</span>
                      </div>
                      <v-switch
                        v-model="compositeAnswers['hasCeo']"
                        color="primary"
                        hide-details
                        density="compact"
                        inset
                      ></v-switch>
                    </div>

                    <!-- 대표자 입력 영역 (스위치 ON일 때만) -->
                    <div class="ceo-inputs" v-if="compositeAnswers['hasCeo']">
                      <div class="input-group-header">
                        <v-icon size="16" color="#f59e0b">mdi-shield-account</v-icon>
                        <span>대표자 (고용·산재·퇴직금 제외)</span>
                      </div>
                      <div class="director-fields">
                        <div class="director-field expense">
                          <label class="field-label">대표자 수</label>
                          <div class="input-wrapper" :class="{ filled: compositeAnswers['ceoCount'], expense: true }">
                            <input
                              type="text"
                              class="main-input"
                              placeholder="1"
                              :value="formatMultiNumberValue('ceoCount')"
                              @input="handleMultiNumberInput($event, 'ceoCount')"
                              @keyup.enter="handleEnterKey"
                            />
                            <span class="input-suffix">명</span>
                          </div>
                        </div>
                        <div class="director-field expense">
                          <label class="field-label">대표자 월 급여</label>
                          <div class="input-wrapper" :class="{ filled: compositeAnswers['ceoSalary'], expense: true }">
                            <input
                              type="text"
                              class="main-input"
                              placeholder="0"
                              :value="formatMultiNumberValue('ceoSalary')"
                              @input="handleMultiNumberInput($event, 'ceoSalary')"
                              @keyup.enter="handleEnterKey"
                            />
                            <span class="input-suffix">원 / 월</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 일반 센터장 입력 영역 -->
                    <div class="director-inputs">
                      <div class="input-group-header">
                        <v-icon size="16" color="#f59e0b">mdi-account-group</v-icon>
                        <span>일반 센터장 (모든 보험·퇴직금 적용)</span>
                      </div>
                      <div class="director-fields">
                        <div class="director-field expense">
                          <label class="field-label">일반 센터장 수</label>
                          <div class="input-wrapper" :class="{ filled: compositeAnswers['directorCount'], expense: true }">
                            <input
                              type="text"
                              class="main-input"
                              placeholder="0"
                              :value="formatMultiNumberValue('directorCount')"
                              @input="handleMultiNumberInput($event, 'directorCount')"
                              @keyup.enter="handleEnterKey"
                            />
                            <span class="input-suffix">명</span>
                          </div>
                        </div>
                        <div class="director-field expense">
                          <label class="field-label">일반 센터장 월 급여</label>
                          <div class="input-wrapper" :class="{ filled: compositeAnswers['directorSalary'], expense: true }">
                            <input
                              type="text"
                              class="main-input"
                              placeholder="0"
                              :value="formatMultiNumberValue('directorSalary')"
                              @input="handleMultiNumberInput($event, 'directorSalary')"
                              @keyup.enter="handleEnterKey"
                            />
                            <span class="input-suffix">원 / 월</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 사회보험부담금 미리보기 -->
                    <div class="insurance-preview" v-if="directorInsurancePreview">
                      <div class="insurance-header">
                        <v-icon size="18" color="#6b7280">mdi-shield-account</v-icon>
                        <span>사회보험부담금 + 퇴직금 (사업자 부담분)</span>
                      </div>
                      <div class="insurance-summary">
                        <!-- 대표자 합계 -->
                        <template v-if="directorInsurancePreview.ceo">
                          <div class="summary-row">
                            <span class="summary-label">대표자 연간 급여</span>
                            <span class="summary-value">{{ formatMoney(directorInsurancePreview.ceo.annualSalary) }}원</span>
                          </div>
                          <div class="summary-row">
                            <span class="summary-label">대표자 연간 사회보험부담금</span>
                            <span class="summary-value">{{ formatMoney(directorInsurancePreview.ceo.annualInsurance) }}원</span>
                          </div>
                        </template>
                        <!-- 일반 센터장 합계 -->
                        <template v-if="directorInsurancePreview.director">
                          <div class="summary-row">
                            <span class="summary-label">일반 센터장 연간 급여</span>
                            <span class="summary-value">{{ formatMoney(directorInsurancePreview.director.annualSalary) }}원</span>
                          </div>
                          <div class="summary-row">
                            <span class="summary-label">일반 센터장 연간 사회보험+퇴직금</span>
                            <span class="summary-value">{{ formatMoney(directorInsurancePreview.director.annualInsurance) }}원</span>
                          </div>
                        </template>
                        <!-- 총합계 -->
                        <div class="summary-row total">
                          <span class="summary-label">총 연간 비용</span>
                          <span class="summary-value highlight">{{ formatMoney(directorInsurancePreview.totalAnnual) }}원</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- 사회복지사 급여 입력 (social-worker-salary) - 대표자/일반 구분 -->
                <template v-else-if="currentQuestion?.inputType === 'social-worker-salary'">
                  <div class="social-worker-salary-section">
                    <!-- 대표자 여부 스위치 -->
                    <div class="ceo-toggle-row">
                      <div class="toggle-label">
                        <v-icon size="20" color="#6b7280">mdi-account-tie</v-icon>
                        <span>대표자(사업주)가 사회복지사로 근무합니까?</span>
                      </div>
                      <v-switch
                        v-model="compositeAnswers['hasCeo']"
                        color="primary"
                        hide-details
                        density="compact"
                        inset
                      ></v-switch>
                    </div>

                    <!-- 대표자 입력 영역 (스위치 ON일 때만) -->
                    <div class="ceo-inputs" v-if="compositeAnswers['hasCeo']">
                      <div class="input-group-header">
                        <v-icon size="16" color="#f59e0b">mdi-shield-account</v-icon>
                        <span>대표자 (고용·산재·퇴직금 제외)</span>
                      </div>
                      <div class="director-fields">
                        <div class="director-field expense">
                          <label class="field-label">대표자 수</label>
                          <div class="input-wrapper" :class="{ filled: compositeAnswers['ceoCount'], expense: true }">
                            <input
                              type="text"
                              class="main-input"
                              placeholder="1"
                              :value="formatMultiNumberValue('ceoCount')"
                              @input="handleMultiNumberInput($event, 'ceoCount')"
                              @keyup.enter="handleEnterKey"
                            />
                            <span class="input-suffix">명</span>
                          </div>
                        </div>
                        <div class="director-field expense">
                          <label class="field-label">대표자 월 급여</label>
                          <div class="input-wrapper" :class="{ filled: compositeAnswers['ceoSalary'], expense: true }">
                            <input
                              type="text"
                              class="main-input"
                              placeholder="0"
                              :value="formatMultiNumberValue('ceoSalary')"
                              @input="handleMultiNumberInput($event, 'ceoSalary')"
                              @keyup.enter="handleEnterKey"
                            />
                            <span class="input-suffix">원 / 월</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 일반 사회복지사 입력 영역 -->
                    <div class="director-inputs">
                      <div class="input-group-header">
                        <v-icon size="16" color="#f59e0b">mdi-account-group</v-icon>
                        <span>일반 사회복지사 (모든 보험·퇴직금 적용)</span>
                      </div>
                      <div class="director-fields">
                        <div class="director-field expense">
                          <label class="field-label">사회복지사 수</label>
                          <div class="input-wrapper" :class="{ filled: compositeAnswers['workerCount'], expense: true }">
                            <input
                              type="text"
                              class="main-input"
                              placeholder="0"
                              :value="formatMultiNumberValue('workerCount')"
                              @input="handleMultiNumberInput($event, 'workerCount')"
                              @keyup.enter="handleEnterKey"
                            />
                            <span class="input-suffix">명</span>
                          </div>
                        </div>
                        <div class="director-field expense">
                          <label class="field-label">월 급여</label>
                          <div class="input-wrapper" :class="{ filled: compositeAnswers['workerSalary'], expense: true }">
                            <input
                              type="text"
                              class="main-input"
                              placeholder="0"
                              :value="formatMultiNumberValue('workerSalary')"
                              @input="handleMultiNumberInput($event, 'workerSalary')"
                              @keyup.enter="handleEnterKey"
                            />
                            <span class="input-suffix">원 / 월</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 가산금 자동 계산 표시 (세입) -->
                    <div class="bonus-preview" v-if="socialWorkerBonusPreview">
                      <div class="bonus-header">
                        <v-icon size="18" color="#6b7280">mdi-cash-plus</v-icon>
                        <span>가산금수입 (세입 자동 반영)</span>
                      </div>
                      <div class="bonus-formula">
                        <div class="formula-row">
                          <span class="formula-label">가산점수</span>
                          <span class="formula-value">{{ socialWorkerBonusPreview.bonusPoint }}점</span>
                          <span class="formula-note">({{ socialWorkerBonusPreview.count }}명 기준)</span>
                        </div>
                        <div class="formula-row">
                          <span class="formula-label">월 가산금</span>
                          <span class="formula-value highlight">{{ formatMoney(socialWorkerBonusPreview.monthlyBonus) }}원</span>
                        </div>
                        <div class="formula-row annual">
                          <span class="formula-label">연간 가산금</span>
                          <span class="formula-value">{{ formatMoney(socialWorkerBonusPreview.annualBonus) }}원</span>
                        </div>
                      </div>
                      <div class="bonus-detail">
                        (수급자별 청구금액 × 85% ÷ 청구인원수) × 가산점수 × 1.8
                      </div>
                    </div>

                    <!-- 사회보험부담금 미리보기 -->
                    <div class="insurance-preview" v-if="socialWorkerInsurancePreview">
                      <div class="insurance-header">
                        <v-icon size="18" color="#6b7280">mdi-shield-account</v-icon>
                        <span>사회보험부담금 + 퇴직금 (사업자 부담분)</span>
                      </div>
                      <div class="insurance-summary">
                        <!-- 대표자 합계 -->
                        <template v-if="socialWorkerInsurancePreview.ceo">
                          <div class="summary-row">
                            <span class="summary-label">대표자 연간 급여</span>
                            <span class="summary-value">{{ formatMoney(socialWorkerInsurancePreview.ceo.annualSalary) }}원</span>
                          </div>
                          <div class="summary-row">
                            <span class="summary-label">대표자 연간 사회보험부담금</span>
                            <span class="summary-value">{{ formatMoney(socialWorkerInsurancePreview.ceo.annualInsurance) }}원</span>
                          </div>
                        </template>
                        <!-- 일반 사회복지사 합계 -->
                        <template v-if="socialWorkerInsurancePreview.worker">
                          <div class="summary-row">
                            <span class="summary-label">사회복지사 연간 급여</span>
                            <span class="summary-value">{{ formatMoney(socialWorkerInsurancePreview.worker.annualSalary) }}원</span>
                          </div>
                          <div class="summary-row">
                            <span class="summary-label">사회복지사 연간 사회보험+퇴직금</span>
                            <span class="summary-value">{{ formatMoney(socialWorkerInsurancePreview.worker.annualInsurance) }}원</span>
                          </div>
                        </template>
                        <!-- 총합계 -->
                        <div class="summary-row total">
                          <span class="summary-label">총 연간 비용</span>
                          <span class="summary-value highlight">{{ formatMoney(socialWorkerInsurancePreview.totalAnnual) }}원</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- 토글 입력 -->
                <template v-else-if="currentQuestion?.inputType === 'toggle'">
                  <div class="toggle-group">
                    <button 
                      class="toggle-option yes" 
                      :class="{ selected: currentAnswer === 'yes' }"
                      @click="currentAnswer = 'yes'"
                    >
                      <div class="toggle-icon">
                        <v-icon size="28">mdi-check-circle</v-icon>
                      </div>
                      <span class="toggle-text">예</span>
                      <span class="toggle-desc">비용을 책정합니다</span>
                    </button>
                    <button 
                      class="toggle-option no" 
                      :class="{ selected: currentAnswer === 'no' }"
                      @click="currentAnswer = 'no'"
                    >
                      <div class="toggle-icon">
                        <v-icon size="28">mdi-close-circle</v-icon>
                      </div>
                      <span class="toggle-text">아니오</span>
                      <span class="toggle-desc">책정하지 않습니다</span>
                    </button>
                  </div>
                </template>

                <!-- 계산 미리보기 -->
                <div class="calc-preview" :class="currentSection" v-if="calculatedPreview && currentQuestion?.inputType === 'number'">
                  <div class="preview-header">
                    <v-icon size="16">mdi-calculator</v-icon>
                    자동 계산 결과
                  </div>
                  <div class="preview-content">
                    <div class="preview-item">
                      <span class="preview-label">월 환산</span>
                      <span class="preview-value">{{ formatMoney(calculatedPreview.monthly) }}원</span>
                    </div>
                    <div class="preview-arrow">
                      <v-icon size="20">mdi-arrow-right-bold</v-icon>
                    </div>
                    <div class="preview-item highlight">
                      <span class="preview-label">연간 합계</span>
                      <span class="preview-value">{{ formatMoney(calculatedPreview.annual) }}원</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 하단 버튼 -->
              <div class="action-buttons">
                <button 
                  class="btn-prev" 
                  @click="prevQuestion"
                  :disabled="currentStep <= 1"
                >
                  <v-icon size="20">mdi-arrow-left</v-icon>
                  이전
                </button>

                <button 
                  class="btn-next" 
                  :class="currentSection"
                  @click="nextQuestion"
                  :disabled="!isCurrentAnswerValid"
                  v-if="currentStep < totalQuestions"
                >
                  다음
                  <v-icon size="20">mdi-arrow-right</v-icon>
                </button>
                
                <button 
                  class="btn-submit" 
                  @click="submitBudget"
                  :disabled="!isAllAnswered"
                  v-else
                >
                  <v-icon size="20">mdi-check-bold</v-icon>
                  예산 생성 완료
                </button>
              </div>
            </div>
          </transition>

          <!-- 잔액 경고 -->
          <div class="balance-alert" v-if="balance < 0 && answeredCount > 5">
            <v-icon size="20" color="#ef4444">mdi-alert</v-icon>
            <span>세출이 세입보다 <strong>{{ formatMoney(Math.abs(balance)) }}원</strong> 초과합니다</span>
          </div>
        </template>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';

const store = useStore();

// 수가 인터페이스 (props용)
interface SugaData {
  suga_idx: number;
  suga_biz_gubun: string;
  suga_year: string;
  suga_rank: string;
  suga_money: number;
  suga_bonin: number;
  suga_wdate: string;
}

// 세금/보험 요율 인터페이스
interface TaxData {
  tr_idx?: number;
  tr_year?: string;
  tr_time_pay?: number;   // 최저시급
  tr_yeon?: number;       // 국민연금 요율
  tr_gun?: number;        // 건강보험 요율
  tr_go?: number;         // 고용보험 요율
  tr_jang?: number;       // 장기요양보험 요율 (건강보험 기준)
  tr_sil?: number;        // 실업급여 요율
  tr_san?: number;        // 산재보험 요율
  tr_labor_cost_rate?: number; // 인건비 지출 비율
}

// 소득세 테이블 인터페이스
interface SojiData {
  income1: number;
  income2: number;
  depe_1: number;
}

// Props & Emits
const props = defineProps<{
  year?: string;
  sugaList?: SugaData[];
  sugaYear?: string;
  sugaIsFallback?: boolean;
  sugaError?: string;
  sugaLoading?: boolean;
  taxData?: TaxData;
  sojiData?: SojiData[];
  taxLoading?: boolean;
  taxError?: string;
  budgetClass?: string; // 추경차수: '', '1', '2', '3' 등 (빈 문자열 = 본예산)
}>();

const emit = defineEmits<{
  (e: 'complete', data: BudgetData): void;
  (e: 'cancel'): void;
  (e: 'year-change', year: string): void;
}>();

// 연도 범위
const currentSystemYear = new Date().getFullYear();
const minYear = currentSystemYear - 2;
const maxYear = currentSystemYear + 1;

// 연도 변경 함수
const changeYear = (delta: number) => {
  const newYear = parseInt(searchParams.value.lebYear) + delta;
  if (newYear >= minYear && newYear <= maxYear) {
    searchParams.value.lebYear = newYear.toString();
    emit('year-change', searchParams.value.lebYear);
  }
};

// 추경차수 관련 computed
const budgetClassLabel = computed(() => {
  const budClass = props.budgetClass || '';
  if (!budClass || budClass === '0') return '본예산';
  return `${budClass}차 추경`;
});

const budgetClassStyle = computed(() => {
  const budClass = props.budgetClass || '';
  if (!budClass || budClass === '0') return 'primary';
  return 'warning';
});

const budgetClassIcon = computed(() => {
  const budClass = props.budgetClass || '';
  if (!budClass || budClass === '0') return 'mdi-file-document-outline';
  return 'mdi-file-document-edit-outline';
});

// 인터페이스
interface QuestionOption {
  value: string | number;
  label: string;
}

interface QuestionField {
  key: string;
  label: string;
  options?: QuestionOption[];
  type?: 'select' | 'number' | 'toggle';
  scode?: string;        // hb_scode
  suffix?: string;
  funding?: string;      // hb_funding
}

// 보험 적용 설정 인터페이스
interface InsuranceConfig {
  nationalPension?: boolean;      // 국민연금
  healthInsurance?: boolean;      // 건강보험
  longTermCare?: boolean;         // 장기요양보험
  employmentInsurance?: boolean;  // 고용보험
  unemployment?: boolean;         // 실업급여 (사업자)
  industrialAccident?: boolean;   // 산재보험
  retirement?: boolean;           // 퇴직금
}

interface Question {
  code: string;
  section: '세입' | '세출';
  sequence: number;
  question: string;
  shortLabel?: string;
  inputType: 'number' | 'select' | 'composite' | 'toggle' | 'multi-number' | 'salary' | 'caregiver-salary' | 'social-worker-salary' | 'director-salary';
  frequency: 'monthly' | 'once' | 'annual' | 'count' | 'reference';
  hint?: string;
  description?: string;
  placeholder?: string;
  suffix?: string;
  options?: QuestionOption[];
  fields?: QuestionField[];
  defaultValue?: string | number;
  disabled?: boolean;
  scode?: string;                  // hb_scode (salary 타입용)
  funding?: string;                // hb_funding
  insuranceConfig?: InsuranceConfig; // 보험 적용 설정 (salary 타입용)
}

interface BudgetData {
  year: string;
  answers: Record<string, string | number>;
  calculations: {
    totalRevenue: number;
    totalExpense: number;
    balance: number;
    bonusRevenue: number;
    items: BudgetItem[];
  };
  budgetEntries?: HhBudgetEntry[]; // hh_budget 테이블에 저장할 데이터
}

interface BudgetItem {
  code: string;
  section: string;
  label: string;
  monthly: number;
  annual: number;
}

// hh_budget 테이블 저장용 인터페이스
interface HhBudgetEntry {
  m_sisul_code: string;     // 시설코드
  hb_year: string;          // 연도
  hb_gubun: 'IN' | 'OUT';   // 구분 (세입/세출)
  hb_scode: string;         // 계정코드 (fc_scode)
  hb_bud_class: string;     // 예산분류
  hb_memo: string;          // 메모/항목명
  hb_price: string;         // 단가
  hb_person: string;        // 명(회)
  hb_month: string;         // 개월
  hb_amount: string;        // 금액
  hb_funding: string;       // 재원구분
  pay_rel_group?: string;   // 급여-보험-퇴직금 연결 그룹 (임시 식별자, 백엔드에서 hb_pay_rel로 변환)
  pay_rel_type?: 'salary' | 'insurance' | 'retirement'; // 급여/보험/퇴직금 타입
}

// 상태
const searchParams = ref({
  lebYear: props.year || store.state.lebYear || new Date().getFullYear().toString(),
});

const currentStep = ref(1);
const answers = ref<Record<string, string | number>>({});
const compositeAnswers = ref<Record<string, string | number | boolean>>({});
const currentAnswer = ref<string>('');
const inputFocused = ref(false);

// hh_budget 테이블 저장용 데이터
const budgetEntries = ref<HhBudgetEntry[]>([]);

// 세입 질문
const revenueQuestions = ref<Question[]>([
  {
    code: 'revenue_target_clients',
    section: '세입',
    sequence: 1,
    question: '예산년도 목표 수급자수는 몇 명입니까?',
    shortLabel: '목표 수급자수',
    inputType: 'number',
    frequency: 'count',
    hint: '결산년도 대비 30% 이상 권장',
    description: '① 예산은 계획이므로 되도록 많이 책정하는 것이 추경의 횟수를 줄일 수 있다. ② 수급자별 평균 서비스 제공시간은 180분 20회로 한정한다. ③ 가족요양은 별도로 추가하지 않는다. ④ 위의 기준은 예산년도에 발생되는 수급자수, 감경적용여부, 서비스 제공시간 등을 특정할 수 없기 때문이므로, 수정을 원하시면 절차가 마무리 되면, 추후 직접 수정하시면 됩니다.',
    placeholder: '수급자 수',
    suffix: '명',
  },
  {
    code: 'revenue_loans_transfer',
    section: '세입',
    sequence: 2,
    question: '금융기관 차입금 / 기타차입금 / 기타전입금이 있습니까?',
    shortLabel: '차입금/전입금',
    inputType: 'multi-number',
    frequency: 'once',
    description: '① 1년 이상 적립하여 상환하여야 할 상황인 경우에는 \'금융기관 차입\' 또는 \'기타 차입\'을 하는 것이 좋다. 차입을 하는 경우에는 지자체에 차입금 관련 보고서를 제출하여 승인을 받은 이후에 예산에 반영이 가능합니다. ② 하지만 1년 이내 상환이 가능한 금액은 차입보다는 전입을 하고, 기타전출금으로 처리하는 것을 권장합니다. 전입의 경우는 지자체의 승인이 불필요합니다.',
    fields: [
      { key: 'bankLoan', label: '금융기관 차입금', type: 'number', scode: '711', suffix: '원', funding: '04' },
      { key: 'otherLoan', label: '기타차입금', type: 'number', scode: '712', suffix: '원', funding: '04' },
      { key: 'otherTransfer', label: '기타전입금', type: 'number', scode: '813', suffix: '원', funding: '04' },
    ],
  },
  {
    code: 'revenue_carryover_misc',
    section: '세입',
    sequence: 3,
    question: '전년도이월금 / 기타예금이자수입 / 기타잡수입이 있습니까?',
    shortLabel: '이월금/이자/잡수입',
    inputType: 'multi-number',
    frequency: 'once',
    description: '① 전년도이월금: 결산연도 말에 예상되는 잔고를 산입 ② 기타예금이자수입: 예금이자는 1,000원 ~ 10,000원으로 산입 ③ 기타잡수입: 정부의 인건비 지원금 또는 각종 잡수입 산입',
    fields: [
      { key: 'carryover', label: '전년도이월금', type: 'number', scode: '911', suffix: '원', funding: '04' },
      { key: 'interest', label: '기타예금이자수입', type: 'number', scode: '1012', suffix: '원', funding: '04' },
      { key: 'misc', label: '기타잡수입', type: 'number', scode: '1014', suffix: '원', funding: '04' },
    ],
  },
]);

// 세출 질문
const expenseQuestions = ref<Question[]>([
  {
    code: 'expense_director_salary',
    section: '세출',
    sequence: 1,
    question: '센터장의 월 임금은 얼마입니까?',
    shortLabel: '센터장 급여',
    inputType: 'director-salary', // 대표자/비대표자 구분 가능한 새 타입
    frequency: 'monthly',
    description: '① 대표자는 고용보험, 산재보험, 퇴직금이 제외됩니다. ② 대표자가 아닌 센터장은 모든 사회보험과 퇴직금이 적용됩니다.',
    fields: [
      { key: 'hasCeo', label: '대표자 센터장 여부', type: 'toggle' },
      { key: 'ceoSalary', label: '대표자 센터장 월 급여', type: 'number', suffix: '원' },
      { key: 'ceoCount', label: '대표자 센터장 수', type: 'number', suffix: '명' },
      { key: 'directorCount', label: '일반 센터장 수', type: 'number', suffix: '명' },
      { key: 'directorSalary', label: '일반 센터장 월 급여', type: 'number', suffix: '원' },
    ],
    scode: '1112',  // 급여(간접비)
    funding: '',
  },
  {
    code: 'expense_social_worker_salary',
    section: '세출',
    sequence: 2,
    question: '사회복지사 인원 및 월 급여는 얼마입니까?',
    shortLabel: '사회복지사 급여',
    inputType: 'social-worker-salary',
    frequency: 'monthly',
    description: '① 대표자가 사회복지사로 근무하는 경우 대표자 스위치를 켜세요. ② 사회복지사 인원에 따라 가산금이 자동으로 세입에 반영됩니다.',
    fields: [
      { key: 'hasCeo', label: '대표자 사회복지사 여부', type: 'toggle' },
      { key: 'ceoSalary', label: '대표자 사회복지사 월 급여', type: 'number', suffix: '원' },
      { key: 'ceoCount', label: '대표자 사회복지사 수', type: 'number', suffix: '명' },
      { key: 'workerCount', label: '일반 사회복지사 수', type: 'number', suffix: '명' },
      { key: 'workerSalary', label: '일반 사회복지사 월 급여', type: 'number', suffix: '원' },
    ],
    scode: '1111',  // 급여(직접비)
    funding: '06',
  },
  {
    code: 'expense_caregiver_hourly',
    section: '세출',
    sequence: 3,
    question: '예산년도 요양보호사 시간당 인건비는 얼마입니까?',
    shortLabel: '요양보호사 급여',
    inputType: 'caregiver-salary',
    frequency: 'monthly',
    hint: '2025년도 전국 평균 12,800원',
    description: '① 대표자가 요양보호사로 근무하는 경우 대표자 스위치를 켜세요. ② 요양보호사 수의 기본값은 목표 수급자 수와 동일합니다.',
    fields: [
      { key: 'hasCeo', label: '대표자 요양보호사 여부', type: 'toggle' },
      { key: 'ceoHourlyWage', label: '대표자 요양보호사 시급', type: 'number', suffix: '원' },
      { key: 'ceoCount', label: '대표자 요양보호사 수', type: 'number', suffix: '명' },
      { key: 'caregiverCount', label: '일반 요양보호사 수', type: 'number', suffix: '명' },
      { key: 'caregiverHourlyWage', label: '일반 요양보호사 시급', type: 'number', suffix: '원' },
    ],
    scode: '1111',  // 급여(직접비)
    funding: '06',
  },
  {
    code: 'expense_operation_rent',
    section: '세출',
    sequence: 4,
    question: '기관운영비 / 사무실 임차료가 있습니까?',
    shortLabel: '운영비/임차료',
    inputType: 'multi-number',
    frequency: 'monthly',
    description: '① 기관운영비: 기관운영 및 유관기관과의 업무협의를 위한 경비 ② 임차료: 사무실 임차료',
    fields: [
      { key: 'operation', label: '기관운영비', type: 'number', scode: '121', suffix: '원', funding: '' },
      { key: 'rent', label: '사무실 임차료', type: 'number', scode: '135', suffix: '원', funding: '' },
    ],
  },
  {
    code: 'expense_allowance_meeting',
    section: '세출',
    sequence: 5,
    question: '직책보조비 / 회의비가 있습니까?',
    shortLabel: '직책보조비/회의비',
    inputType: 'multi-number',
    frequency: 'monthly',
    description: '① 직책보조비: 영수증 없는 지출. 인건비지출비율이 높을 때 인건비 조절을 위해 활용. 과도한 사용은 탈세혐의 우려 ② 회의비: 월간 회의 및 다과비에 사용되는 경비',
    fields: [
      { key: 'allowance', label: '직책보조비', type: 'number', scode: '122', suffix: '원', funding: '' },
      { key: 'meeting', label: '회의비', type: 'number', scode: '123', suffix: '원', funding: '' },
    ],
  },
  {
    code: 'expense_loan_repayment',
    section: '세출',
    sequence: 6,
    question: '차입금에 대한 상환금이 있습니까?',
    shortLabel: '원금상환금',
    inputType: 'multi-number',
    frequency: 'monthly',
    description: '① 월 상환 계획 금액을 입력합니다.',
    fields: [
      { key: 'repayment', label: '원금상환금', type: 'number', scode: '611', suffix: '원', funding: '' },
    ],
  },
]);

// Computed
const allQuestions = computed<Question[]>(() => {
  return [...revenueQuestions.value, ...expenseQuestions.value];
});

const totalQuestions = computed(() => allQuestions.value.length);

const answeredCount = computed(() => {
  return Object.keys(answers.value).filter(k => answers.value[k] !== '').length;
});

const progressPercent = computed(() => {
  if (totalQuestions.value === 0) return 0;
  return (answeredCount.value / totalQuestions.value) * 100;
});

const progressOffset = computed(() => {
  const circumference = 2 * Math.PI * 45;
  return circumference - (progressPercent.value / 100) * circumference;
});

const currentQuestion = computed<Question>(() => {
  return allQuestions.value[currentStep.value - 1] || revenueQuestions.value[0];
});

const currentSection = computed(() => {
  return currentQuestion.value?.section === '세입' ? 'revenue' : 'expense';
});

const currentSectionQuestions = computed(() => {
  return currentSection.value === 'revenue' ? revenueQuestions.value : expenseQuestions.value;
});

const revenueAnsweredCount = computed(() => {
  return revenueQuestions.value.filter(q => answers.value[q.code]).length;
});

const expenseAnsweredCount = computed(() => {
  return expenseQuestions.value.filter(q => answers.value[q.code]).length;
});

const isRevenueCompleted = computed(() => {
  return revenueAnsweredCount.value === revenueQuestions.value.length;
});

const isExpenseCompleted = computed(() => {
  return expenseAnsweredCount.value === expenseQuestions.value.length;
});

// 180분 수가 데이터 (기준 수가) - props에서 가져옴
const suga180 = computed<SugaData | null>(() => {
  return props.sugaList?.find(s => s.suga_rank === '180분') || null;
});

// 수가 데이터 유효 여부
const hasSugaData = computed(() => {
  return (props.sugaList?.length || 0) > 0 && suga180.value !== null;
});

const isCurrentAnswerValid = computed(() => {
  const question = currentQuestion.value;
  
  if (!question) return false;
  
  if (question.inputType === 'composite') {
    const fields = question.fields || [];
    return fields.every(f => compositeAnswers.value[f.key]);
  }
  
  // multi-number는 선택적 입력 (0 또는 미입력 허용)
  if (question.inputType === 'multi-number') {
    return true;
  }
  
  // salary는 급여가 입력되어야 함 (인원수는 기본값 1)
  if (question.inputType === 'salary') {
    const salary = Number(compositeAnswers.value['salary']) || 0;
    return salary > 0;
  }
  
  // director-salary: 대표자 또는 일반 센터장 중 하나라도 입력되어야 함
  if (question.inputType === 'director-salary') {
    const hasCeo = compositeAnswers.value['hasCeo'];
    const ceoSalary = Number(compositeAnswers.value['ceoSalary']) || 0;
    const ceoCount = Number(compositeAnswers.value['ceoCount']) || 0;
    const directorCount = Number(compositeAnswers.value['directorCount']) || 0;
    const directorSalary = Number(compositeAnswers.value['directorSalary']) || 0;
    
    // 대표자가 있으면 대표자 급여 필수
    if (hasCeo && (ceoSalary <= 0 || ceoCount <= 0)) {
      return false;
    }
    // 일반 센터장이 있으면 일반 센터장 급여 필수
    if (directorCount > 0 && directorSalary <= 0) {
      return false;
    }
    // 대표자 또는 일반 센터장 중 하나는 있어야 함
    return (hasCeo && ceoSalary > 0) || (directorCount > 0 && directorSalary > 0);
  }
  
  // caregiver-salary: 대표자 또는 일반 요양보호사 중 하나라도 입력되어야 함
  if (question.inputType === 'caregiver-salary') {
    const hasCeo = compositeAnswers.value['hasCeo'];
    const ceoHourlyWage = Number(compositeAnswers.value['ceoHourlyWage']) || 0;
    const ceoCount = Number(compositeAnswers.value['ceoCount']) || 0;
    const caregiverCount = Number(compositeAnswers.value['caregiverCount']) || targetClientsCount.value;
    const caregiverHourlyWage = Number(compositeAnswers.value['caregiverHourlyWage']) || 0;
    
    // 대표자가 있으면 대표자 시급 필수
    if (hasCeo && (ceoHourlyWage <= 0 || ceoCount <= 0)) {
      return false;
    }
    // 일반 요양보호사가 있으면 시급 필수
    if (caregiverCount > 0 && caregiverHourlyWage <= 0) {
      return false;
    }
    // 대표자 또는 일반 요양보호사 중 하나는 있어야 함
    return (hasCeo && ceoHourlyWage > 0) || (caregiverCount > 0 && caregiverHourlyWage > 0);
  }
  
  // social-worker-salary: 대표자 또는 일반 사회복지사 중 하나라도 입력되어야 함
  if (question.inputType === 'social-worker-salary') {
    const hasCeo = compositeAnswers.value['hasCeo'];
    const ceoSalary = Number(compositeAnswers.value['ceoSalary']) || 0;
    const ceoCount = Number(compositeAnswers.value['ceoCount']) || 0;
    const workerCount = Number(compositeAnswers.value['workerCount']) || 0;
    const workerSalary = Number(compositeAnswers.value['workerSalary']) || 0;
    
    // 대표자가 있으면 대표자 급여 필수
    if (hasCeo && (ceoSalary <= 0 || ceoCount <= 0)) {
      return false;
    }
    // 일반 사회복지사가 있으면 급여 필수
    if (workerCount > 0 && workerSalary <= 0) {
      return false;
    }
    // 대표자 또는 일반 사회복지사 중 하나는 있어야 함
    return (hasCeo && ceoSalary > 0) || (workerCount > 0 && workerSalary > 0);
  }
  
  if (question.inputType === 'toggle') {
    return currentAnswer.value === 'yes' || currentAnswer.value === 'no';
  }
  
  if (question.defaultValue !== undefined && !currentAnswer.value) {
    return true;
  }
  
  return currentAnswer.value !== '';
});

// 입력 기준 표시 (월/연)
const getPeriodLabel = (frequency: string, section: string): string => {
  if (frequency === 'count' || frequency === 'reference') {
    return ''; // 인원수나 참고용은 표시 안함
  }
  if (frequency === 'monthly') {
    return '/ 월';
  }
  if (frequency === 'once' || frequency === 'annual') {
    return '/ 연';
  }
  return '';
};

// 현재 질문의 기준 표시
const currentPeriodLabel = computed(() => {
  const q = currentQuestion.value;
  if (!q) return '';
  return getPeriodLabel(q.frequency, currentSection.value);
});

// multi-number 필드의 기준 표시 (세입은 연, 세출은 월)
const getMultiNumberPeriodLabel = computed(() => {
  const q = currentQuestion.value;
  if (!q) return '';
  // 세입 multi-number는 1회성(연), 세출 multi-number는 월 단위
  if (q.section === '세입' && q.frequency === 'once') {
    return '/ 연';
  }
  if (q.section === '세출') {
    return '/ 월';
  }
  return getPeriodLabel(q.frequency, currentSection.value);
});

const isAllAnswered = computed(() => {
  return allQuestions.value.every((q, idx) => {
    // 기본값이 있는 질문은 답변 필수 아님
    if (q.defaultValue !== undefined) {
      return true;
    }
    
    // multi-number는 선택적 입력 (0 또는 미입력 허용)
    if (q.inputType === 'multi-number') {
      return true;
    }
    
    // 현재 질문이면 currentAnswer도 확인
    if (idx === currentStep.value - 1) {
      return currentAnswer.value !== '' || (answers.value[q.code] !== undefined && answers.value[q.code] !== '');
    }
    
    return answers.value[q.code] !== undefined && answers.value[q.code] !== '';
  });
});

const calculatedPreview = computed(() => {
  const question = currentQuestion.value;
  if (!question) return null;
  
  const value = parseNumber(currentAnswer.value);
  
  if (!value && question.defaultValue === undefined) return null;
  
  // reference(참고용)는 미리보기 표시 안함
  if (question.frequency === 'reference' || question.frequency === 'count') {
    return null;
  }
  
  const numValue = value || (question.defaultValue as number) || 0;
  
  let monthly = 0;
  let annual = 0;
  
  switch (question.frequency) {
    case 'monthly':
      monthly = numValue;
      annual = numValue * 12;
      break;
    case 'once':
    case 'annual':
      monthly = Math.round(numValue / 12);
      annual = numValue;
      break;
  }
  
  // 사회복지사 급여: 기본 2명 기준
  if (question.code === 'expense_social_worker_salary') {
    const count = 2; // 기본 2명 (Q&A에서 제외됨)
    monthly = numValue * count;
    annual = monthly * 12;
  }
  
  return { monthly, annual };
});

// 사회복지사 인원에 따른 가산점수 계산 (함수)
const getSocialWorkerBonusPoint = (count: number): number => {
  if (count <= 0) return 0;
  if (count === 1) return 1.8;
  if (count === 2) return 3.2;
  // 2명 초과부터 1.2점씩 추가
  return 3.2 + (count - 2) * 1.2;
};

// 가산급여 계산 (세입에 포함) - 사회복지사 인원 기준
const bonusRevenue = computed(() => {
  const targetClients = parseNumber(String(answers.value['revenue_target_clients'])) || 0;
  
  // 사회복지사 인원수 가져오기 (social-worker-salary 질문에서)
  // 현재 질문이 사회복지사 급여면 compositeAnswers 사용, 아니면 저장된 answers 사용
  let socialWorkerCount = 0;
  
  if (currentQuestion.value?.code === 'expense_social_worker_salary') {
    // 현재 입력 중인 값 사용
    const hasCeo = compositeAnswers.value['hasCeo'];
    const ceoCount = hasCeo ? (Number(compositeAnswers.value['ceoCount']) || 0) : 0;
    const workerCount = Number(compositeAnswers.value['workerCount']) || 0;
    socialWorkerCount = ceoCount + workerCount;
  } else {
    // 저장된 값 사용
    const swAnswer = answers.value['expense_social_worker_salary'];
    if (swAnswer && typeof swAnswer === 'string') {
      try {
        const parsed = JSON.parse(swAnswer);
        const hasCeo = parsed['hasCeo'];
        const ceoCount = hasCeo ? (Number(parsed['ceoCount']) || 0) : 0;
        const workerCount = Number(parsed['workerCount']) || 0;
        socialWorkerCount = ceoCount + workerCount;
      } catch { /* ignore */ }
    }
  }
  
  if (targetClients === 0 || socialWorkerCount === 0) return 0;
  
  // 4등급 180분 기준 수가 찾기 (props에서 가져옴)
  const suga = props.sugaList?.find(s => s.suga_rank === '180분');
  if (!suga) return 0;
  
  const sugaMoney = Number(suga.suga_money) || 0;
  
  // 수급자별 청구금액 = 180분 수가 × 20회
  const clientBillingAmount = sugaMoney * 20;
  
  // 가산점수 계산
  const bonusScore = getSocialWorkerBonusPoint(socialWorkerCount);
  
  // 가산식 = (수급자별 청구금액 × 85% ÷ 청구인원수) × 가산점수 × 방문요양 가산유형(1.8)
  const monthlyBonus = Math.round((clientBillingAmount * 0.85 / targetClients) * bonusScore * 1.8 * targetClients);
  
  return monthlyBonus * 12;
});

const totalRevenue = computed(() => {
  let total = 0;
  
  // 180분 수가 데이터
  const suga = props.sugaList?.find(s => s.suga_rank === '180분');
  const sugaMoney = suga ? Number(suga.suga_money) : 0;
  const sugaBonin = suga ? Number(suga.suga_bonin) : 0;
  const sugaGubyo = sugaMoney - sugaBonin; // 급여비용 (공단부담)
  
  revenueQuestions.value.forEach(q => {
    // 목표 수급자수 → 본인부담금수입 + 장기요양급여수입
    if (q.code === 'revenue_target_clients') {
      const value = parseNumber(String(answers.value[q.code])) || 0;
      const targetClients = value;
      if (targetClients > 0 && suga) {
        const boninAmount = sugaBonin * 20 * targetClients * 12;
        const gubyoAmount = sugaGubyo * 20 * targetClients * 12;
        total += boninAmount + gubyoAmount;
      }
    }
    // multi-number (차입금/전입금)
    else if (q.inputType === 'multi-number' && q.fields) {
      const savedAnswer = answers.value[q.code];
      if (savedAnswer && typeof savedAnswer === 'string') {
        try {
          const parsed = JSON.parse(savedAnswer);
          q.fields.forEach(field => {
            const fieldValue = Number(parsed[field.key]) || 0;
            total += fieldValue; // 1회성 금액
          });
        } catch { /* ignore */ }
      }
    }
    else if (q.frequency === 'monthly') {
      const value = parseNumber(String(answers.value[q.code])) || 0;
      total += value * 12;
    } else if (q.frequency === 'once') {
      const value = parseNumber(String(answers.value[q.code])) || 0;
      total += value;
    }
  });
  
  // 가산급여 추가
  total += bonusRevenue.value;
  
  return total;
});

const totalExpense = computed(() => {
  let total = 0;
  
  expenseQuestions.value.forEach(q => {
    // reference(참고용)는 합계에서 제외
    if (q.frequency === 'reference') {
      return;
    }
    
    // multi-number 타입: 각 필드 값 합산
    if (q.inputType === 'multi-number' && q.fields) {
      const savedAnswer = answers.value[q.code];
      if (savedAnswer && typeof savedAnswer === 'string') {
        try {
          const parsed = JSON.parse(savedAnswer);
          q.fields.forEach(field => {
            const fieldValue = Number(parsed[field.key]) || 0;
            total += fieldValue * 12; // 월 금액 × 12개월
          });
        } catch { /* ignore */ }
      }
      return;
    }
    
    // salary 타입: 급여 + 사회보험부담금(기관 기준) 합산
    if (q.inputType === 'salary') {
      const savedAnswer = answers.value[q.code];
      if (savedAnswer && typeof savedAnswer === 'string') {
        try {
          const parsed = JSON.parse(savedAnswer);
          const salary = Number(parsed['salary']) || 0;
          const count = Number(parsed['count']) || 1;
          const totalSalary = salary * count;
          
          // 연간 급여
          const annualSalary = totalSalary * 12;
          
          // 사회보험부담금 계산 - 기관 기준 (실업급여, 산재보험, 퇴직금 포함)
          const config = q.insuranceConfig || {};
          const nationalPension = config.nationalPension ? Math.round(totalSalary * (props.taxData?.tr_yeon || 0)) : 0;
          const healthInsurance = config.healthInsurance ? Math.round(totalSalary * (props.taxData?.tr_gun || 0)) : 0;
          const longTermCare = config.longTermCare ? Math.round(healthInsurance * (props.taxData?.tr_jang || 0)) : 0;
          const employmentInsurance = config.employmentInsurance ? Math.round(totalSalary * (props.taxData?.tr_go || 0)) : 0;
          const unemployment = config.unemployment ? Math.round(totalSalary * (props.taxData?.tr_sil || 0)) : 0;
          const industrialAccident = config.industrialAccident ? Math.round(totalSalary * (props.taxData?.tr_san || 0)) : 0;
          const retirement = config.retirement ? Math.round(totalSalary / 12) : 0;
          
          const monthlyInsurance = nationalPension + healthInsurance + longTermCare + employmentInsurance + unemployment + industrialAccident + retirement;
          const annualInsurance = monthlyInsurance * 12;
          
          total += annualSalary + annualInsurance;
        } catch { /* ignore */ }
      }
      return;
    }
    
    // director-salary 타입: 대표자/일반 센터장 구분
    if (q.inputType === 'director-salary') {
      const savedAnswer = answers.value[q.code];
      if (savedAnswer && typeof savedAnswer === 'string') {
        try {
          const parsed = JSON.parse(savedAnswer);
          const hasCeo = parsed['hasCeo'];
          const ceoSalary = Number(parsed['ceoSalary']) || 0;
          const ceoCount = Number(parsed['ceoCount']) || 0;
          const directorCount = Number(parsed['directorCount']) || 0;
          const directorSalary = Number(parsed['directorSalary']) || 0;
          
          // 대표자 계산 (고용/산재/퇴직금 제외)
          if (hasCeo && ceoSalary > 0 && ceoCount > 0) {
            const totalCeoSalary = ceoSalary * ceoCount;
            const nationalPension = Math.round(totalCeoSalary * (props.taxData?.tr_yeon || 0));
            const healthInsurance = Math.round(totalCeoSalary * (props.taxData?.tr_gun || 0));
            const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
            const monthlyInsurance = nationalPension + healthInsurance + longTermCare;
            total += (totalCeoSalary + monthlyInsurance) * 12;
          }
          
          // 일반 센터장 계산 (모든 보험 + 퇴직금)
          if (directorCount > 0 && directorSalary > 0) {
            const totalDirectorSalary = directorSalary * directorCount;
            const nationalPension = Math.round(totalDirectorSalary * (props.taxData?.tr_yeon || 0));
            const healthInsurance = Math.round(totalDirectorSalary * (props.taxData?.tr_gun || 0));
            const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
            const employmentInsurance = Math.round(totalDirectorSalary * (props.taxData?.tr_go || 0));
            const unemployment = Math.round(totalDirectorSalary * (props.taxData?.tr_sil || 0));
            const industrialAccident = Math.round(totalDirectorSalary * (props.taxData?.tr_san || 0));
            const retirement = Math.round(totalDirectorSalary / 12);
            const monthlyInsurance = nationalPension + healthInsurance + longTermCare + employmentInsurance + unemployment + industrialAccident + retirement;
            total += (totalDirectorSalary + monthlyInsurance) * 12;
          }
        } catch { /* ignore */ }
      }
      return;
    }
    
    // caregiver-salary 타입: 대표자/일반 구분
    if (q.inputType === 'caregiver-salary') {
      const savedAnswer = answers.value[q.code];
      if (savedAnswer && typeof savedAnswer === 'string') {
        try {
          const parsed = JSON.parse(savedAnswer);
          const hasCeo = parsed['hasCeo'];
          const ceoHourlyWage = Number(parsed['ceoHourlyWage']) || 0;
          const ceoCount = Number(parsed['ceoCount']) || 0;
          const caregiverCount = Number(parsed['caregiverCount']) || targetClientsCount.value;
          const caregiverHourlyWage = Number(parsed['caregiverHourlyWage']) || 0;
          
          // 대표자 계산 (고용/산재/퇴직금 제외)
          if (hasCeo && ceoHourlyWage > 0 && ceoCount > 0) {
            const monthlySalary = ceoHourlyWage * 60;
            const totalCeoSalary = monthlySalary * ceoCount;
            const nationalPension = Math.round(totalCeoSalary * (props.taxData?.tr_yeon || 0));
            const healthInsurance = Math.round(totalCeoSalary * (props.taxData?.tr_gun || 0));
            const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
            const monthlyInsurance = nationalPension + healthInsurance + longTermCare;
            total += (totalCeoSalary + monthlyInsurance) * 12;
          }
          
          // 일반 요양보호사 계산 (모든 보험 + 퇴직금)
          if (caregiverCount > 0 && caregiverHourlyWage > 0) {
            const monthlySalary = caregiverHourlyWage * 60;
            const totalCaregiverSalary = monthlySalary * caregiverCount;
            const nationalPension = Math.round(totalCaregiverSalary * (props.taxData?.tr_yeon || 0));
            const healthInsurance = Math.round(totalCaregiverSalary * (props.taxData?.tr_gun || 0));
            const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
            const employmentInsurance = Math.round(totalCaregiverSalary * (props.taxData?.tr_go || 0));
            const unemployment = Math.round(totalCaregiverSalary * (props.taxData?.tr_sil || 0));
            const industrialAccident = Math.round(totalCaregiverSalary * (props.taxData?.tr_san || 0));
            const retirement = Math.round(totalCaregiverSalary / 12);
            const monthlyInsurance = nationalPension + healthInsurance + longTermCare + employmentInsurance + unemployment + industrialAccident + retirement;
            total += (totalCaregiverSalary + monthlyInsurance) * 12;
          }
        } catch { /* ignore */ }
      }
      return;
    }
    
    // social-worker-salary 타입: 대표자/일반 구분
    if (q.inputType === 'social-worker-salary') {
      const savedAnswer = answers.value[q.code];
      if (savedAnswer && typeof savedAnswer === 'string') {
        try {
          const parsed = JSON.parse(savedAnswer);
          const hasCeo = parsed['hasCeo'];
          const ceoSalary = Number(parsed['ceoSalary']) || 0;
          const ceoCount = Number(parsed['ceoCount']) || 0;
          const workerCount = Number(parsed['workerCount']) || 0;
          const workerSalary = Number(parsed['workerSalary']) || 0;
          
          // 대표자 계산 (고용/산재/퇴직금 제외)
          if (hasCeo && ceoSalary > 0 && ceoCount > 0) {
            const totalCeoSalary = ceoSalary * ceoCount;
            const nationalPension = Math.round(totalCeoSalary * (props.taxData?.tr_yeon || 0));
            const healthInsurance = Math.round(totalCeoSalary * (props.taxData?.tr_gun || 0));
            const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
            const monthlyInsurance = nationalPension + healthInsurance + longTermCare;
            total += (totalCeoSalary + monthlyInsurance) * 12;
          }
          
          // 일반 사회복지사 계산 (모든 보험 + 퇴직금)
          if (workerCount > 0 && workerSalary > 0) {
            const totalWorkerSalary = workerSalary * workerCount;
            const nationalPension = Math.round(totalWorkerSalary * (props.taxData?.tr_yeon || 0));
            const healthInsurance = Math.round(totalWorkerSalary * (props.taxData?.tr_gun || 0));
            const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
            const employmentInsurance = Math.round(totalWorkerSalary * (props.taxData?.tr_go || 0));
            const unemployment = Math.round(totalWorkerSalary * (props.taxData?.tr_sil || 0));
            const industrialAccident = Math.round(totalWorkerSalary * (props.taxData?.tr_san || 0));
            const retirement = Math.round(totalWorkerSalary / 12);
            const monthlyInsurance = nationalPension + healthInsurance + longTermCare + employmentInsurance + unemployment + industrialAccident + retirement;
            total += (totalWorkerSalary + monthlyInsurance) * 12;
          }
        } catch { /* ignore */ }
      }
      return;
    }
    
    const value = parseNumber(String(answers.value[q.code])) || 0;
    
    // 사회복지사 급여: 기본 2명 × 월급 × 12 (legacy - 제거 예정)
    if (q.code === 'expense_social_worker_salary_legacy') {
      const count = 2; // 기본 2명 (Q&A에서 제외됨)
      total += value * count * 12;
    } else if (q.frequency === 'monthly') {
      total += value * 12;
    } else if (q.frequency === 'once') {
      total += value;
    }
    // count는 합계에서 제외 (인원수)
  });
  
  return total;
});

const balance = computed(() => {
  return totalRevenue.value - totalExpense.value;
});

// 기준 인건비 지출 비율 (hh_tax_rate 테이블의 tr_labor_cost_rate)
const standardLaborCostRate = computed(() => {
  return props.taxData?.tr_labor_cost_rate || 60; // 기본값 60%
});

// 인건비 총액 계산 (scode 기준: 1111 + 1151 + 1141, 시설장 제외)
// 1111: 급여(직접비), 1151: 사회보험부담금(직접비), 1141: 퇴직금(직접비)
const totalLaborCost = computed(() => {
  let total = 0;
  
  expenseQuestions.value.forEach(q => {
    // caregiver-salary, social-worker-salary 타입만 (시설장은 간접비 1112, 1152, 1142 사용하므로 제외)
    if (q.inputType === 'caregiver-salary' || q.inputType === 'social-worker-salary') {
      // 현재 질문이면 compositeAnswers 사용, 아니면 저장된 answers 사용
      let parsed: Record<string, string | number | boolean> = {};
      
      if (currentQuestion.value?.code === q.code) {
        parsed = { ...compositeAnswers.value };
      } else {
        const savedAnswer = answers.value[q.code];
        if (savedAnswer && typeof savedAnswer === 'string') {
          try {
            parsed = JSON.parse(savedAnswer);
          } catch { /* ignore */ }
        }
      }
      
      if (q.inputType === 'caregiver-salary') {
        // 대표자 요양보호사 (고용/산재/퇴직금 제외)
        const hasCeo = parsed['hasCeo'];
        if (hasCeo) {
          const ceoHourlyWage = Number(parsed['ceoHourlyWage']) || 0;
          const ceoCount = Number(parsed['ceoCount']) || 0;
          if (ceoHourlyWage > 0 && ceoCount > 0) {
            const monthlySalary = ceoHourlyWage * 60 * ceoCount;
            const annualSalary = monthlySalary * 12;
            // 대표자: 국민연금, 건강보험, 장기요양만
            const nationalPension = Math.round(monthlySalary * (props.taxData?.tr_yeon || 0));
            const healthInsurance = Math.round(monthlySalary * (props.taxData?.tr_gun || 0));
            const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
            const monthlyInsurance = nationalPension + healthInsurance + longTermCare;
            total += annualSalary + (monthlyInsurance * 12);
          }
        }
        
        // 일반 요양보호사 (모든 보험 + 퇴직금)
        const caregiverHourlyWage = Number(parsed['caregiverHourlyWage']) || 0;
        const caregiverCount = Number(parsed['caregiverCount']) || targetClientsCount.value;
        if (caregiverHourlyWage > 0 && caregiverCount > 0) {
          const monthlySalary = caregiverHourlyWage * 60 * caregiverCount;
          const annualSalary = monthlySalary * 12;
          const nationalPension = Math.round(monthlySalary * (props.taxData?.tr_yeon || 0));
          const healthInsurance = Math.round(monthlySalary * (props.taxData?.tr_gun || 0));
          const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
          const employmentInsurance = Math.round(monthlySalary * (props.taxData?.tr_go || 0));
          const unemployment = Math.round(monthlySalary * (props.taxData?.tr_sil || 0));
          const industrialAccident = Math.round(monthlySalary * (props.taxData?.tr_san || 0));
          const retirement = Math.round(monthlySalary / 12);
          const monthlyInsurance = nationalPension + healthInsurance + longTermCare + employmentInsurance + unemployment + industrialAccident + retirement;
          total += annualSalary + (monthlyInsurance * 12);
        }
      } else if (q.inputType === 'social-worker-salary') {
        // 대표자 사회복지사 (고용/산재/퇴직금 제외)
        const hasCeo = parsed['hasCeo'];
        if (hasCeo) {
          const ceoSalary = Number(parsed['ceoSalary']) || 0;
          const ceoCount = Number(parsed['ceoCount']) || 0;
          if (ceoSalary > 0 && ceoCount > 0) {
            const monthlySalary = ceoSalary * ceoCount;
            const annualSalary = monthlySalary * 12;
            // 대표자: 국민연금, 건강보험, 장기요양만
            const nationalPension = Math.round(monthlySalary * (props.taxData?.tr_yeon || 0));
            const healthInsurance = Math.round(monthlySalary * (props.taxData?.tr_gun || 0));
            const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
            const monthlyInsurance = nationalPension + healthInsurance + longTermCare;
            total += annualSalary + (monthlyInsurance * 12);
          }
        }
        
        // 일반 사회복지사 (모든 보험 + 퇴직금)
        const workerSalary = Number(parsed['workerSalary']) || 0;
        const workerCount = Number(parsed['workerCount']) || 0;
        if (workerSalary > 0 && workerCount > 0) {
          const monthlySalary = workerSalary * workerCount;
          const annualSalary = monthlySalary * 12;
          const nationalPension = Math.round(monthlySalary * (props.taxData?.tr_yeon || 0));
          const healthInsurance = Math.round(monthlySalary * (props.taxData?.tr_gun || 0));
          const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
          const employmentInsurance = Math.round(monthlySalary * (props.taxData?.tr_go || 0));
          const unemployment = Math.round(monthlySalary * (props.taxData?.tr_sil || 0));
          const industrialAccident = Math.round(monthlySalary * (props.taxData?.tr_san || 0));
          const retirement = Math.round(monthlySalary / 12);
          const monthlyInsurance = nationalPension + healthInsurance + longTermCare + employmentInsurance + unemployment + industrialAccident + retirement;
          total += annualSalary + (monthlyInsurance * 12);
        }
      }
    }
  });
  
  return total;
});

// 인건비 비율 계산용 수입 (scode 기준: 112 + 611 + 613)
const laborCostBaseRevenue = computed(() => {
  // 180분 수가 데이터
  const suga = props.sugaList?.find(s => s.suga_rank === '180분');
  if (!suga) return 0;
  
  const sugaMoney = Number(suga.suga_money) || 0;
  const sugaBonin = Number(suga.suga_bonin) || 0;
  const sugaGubyo = sugaMoney - sugaBonin; // 급여비용 (공단부담)
  
  // 목표 수급자수 가져오기 (현재 입력 중이면 compositeAnswers, 아니면 answers)
  let targetClients = 0;
  if (currentQuestion.value?.code === 'revenue_target_clients') {
    targetClients = parseNumber(String(currentAnswer.value)) || 0;
  } else {
    targetClients = parseNumber(String(answers.value['revenue_target_clients'])) || 0;
  }
  
  if (targetClients <= 0) return 0;
  
  // scode 112: 본인부담금수입
  const scode112 = sugaBonin * 20 * targetClients * 12;
  // scode 611: 장기요양급여수입
  const scode611 = sugaGubyo * 20 * targetClients * 12;
  // scode 613: 가산금수입 (사회복지사)
  const scode613 = bonusRevenue.value;
  
  return scode112 + scode611 + scode613;
});

// 현재 인건비 지출 비율 = 지출(요양보호사,사회복지사 급여,보험,퇴직금) / 수입(장기요양급여수입, 본인부담금수입, 가산금) × 100
const currentLaborCostRate = computed(() => {
  if (laborCostBaseRevenue.value === 0) return 0;
  return (totalLaborCost.value / laborCostBaseRevenue.value) * 100;
});

// 인건비 비율 기준 미달 여부 (기준비율보다 현재비율이 낮으면 미달)
const isLaborCostBelowStandard = computed(() => {
  return currentLaborCostRate.value < standardLaborCostRate.value;
});

// 인건비 비율 산출근거 툴팁 텍스트
const laborCostTooltip = computed(() => {
  const laborCost = totalLaborCost.value;
  const baseRevenue = laborCostBaseRevenue.value;
  const rate = currentLaborCostRate.value;
  
  return `산출근거:\n` +
    `인건비 지출 = ${formatMoney(laborCost)}원\n` +
    `(요양보호사·사회복지사 급여+보험+퇴직금)\n\n` +
    `수입 = ${formatMoney(baseRevenue)}원\n` +
    `(장기요양급여+본인부담금+가산금)\n\n` +
    `비율 = ${formatMoney(laborCost)} ÷ ${formatMoney(baseRevenue)} × 100\n` +
    `     = ${rate.toFixed(1)}%`;
});

// salary 타입의 사회보험부담금 미리보기 계산
const salaryInsurancePreview = computed(() => {
  const question = currentQuestion.value;
  
  if (!question || question.inputType !== 'salary') {
    return null;
  }
  
  const salary = Number(compositeAnswers.value['salary']) || 0;
  const count = Number(compositeAnswers.value['count']) || 1;
  
  if (salary <= 0) {
    return null;
  }
  
  const config = question.insuranceConfig || {};
  const totalSalary = salary * count; // 총 급여 (월급 × 인원수)
  
  // 사업자 부담 계산
  const nationalPension = config.nationalPension ? Math.round(totalSalary * (props.taxData?.tr_yeon || 0)) : 0;
  const healthInsurance = config.healthInsurance ? Math.round(totalSalary * (props.taxData?.tr_gun || 0)) : 0;
  const longTermCare = config.longTermCare ? Math.round(healthInsurance * (props.taxData?.tr_jang || 0)) : 0;
  const employmentInsurance = config.employmentInsurance ? Math.round(totalSalary * (props.taxData?.tr_go || 0)) : 0;
  const unemployment = config.unemployment ? Math.round(totalSalary * (props.taxData?.tr_sil || 0)) : 0;
  const industrialAccident = config.industrialAccident ? Math.round(totalSalary * (props.taxData?.tr_san || 0)) : 0;
  const retirement = config.retirement ? Math.round(totalSalary / 12) : 0;
  
  const total = nationalPension + healthInsurance + longTermCare + employmentInsurance + unemployment + industrialAccident + retirement;
  const annualSalary = totalSalary * 12; // 연간 급여
  const annualInsurance = total * 12;    // 연간 보험료
  const annualTotal = annualSalary + annualInsurance; // 연간 총 비용
  
  return {
    nationalPension,
    healthInsurance,
    longTermCare,
    employmentInsurance,
    unemployment,
    industrialAccident,
    retirement,
    total,
    annualSalary,
    annualInsurance,
    annualTotal
  };
});

// director-salary 타입의 사회보험부담금 미리보기 계산 (대표자/일반 센터장 구분)
const directorInsurancePreview = computed(() => {
  const question = currentQuestion.value;
  
  if (!question || question.inputType !== 'director-salary') {
    return null;
  }
  
  const hasCeo = compositeAnswers.value['hasCeo'];
  const ceoSalary = Number(compositeAnswers.value['ceoSalary']) || 0;
  const ceoCount = Number(compositeAnswers.value['ceoCount']) || 0;
  const directorCount = Number(compositeAnswers.value['directorCount']) || 0;
  const directorSalary = Number(compositeAnswers.value['directorSalary']) || 0;
  
  let result: {
    ceo?: { annualSalary: number; annualInsurance: number };
    director?: { annualSalary: number; annualInsurance: number };
    totalAnnual: number;
  } = { totalAnnual: 0 };
  
  // 대표자 계산 (고용/산재/퇴직금 제외)
  if (hasCeo && ceoSalary > 0 && ceoCount > 0) {
    const totalCeoSalary = ceoSalary * ceoCount;
    const nationalPension = Math.round(totalCeoSalary * (props.taxData?.tr_yeon || 0));
    const healthInsurance = Math.round(totalCeoSalary * (props.taxData?.tr_gun || 0));
    const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
    // 대표자: 고용보험, 산재보험, 퇴직금 제외
    const monthlyInsurance = nationalPension + healthInsurance + longTermCare;
    
    result.ceo = {
      annualSalary: totalCeoSalary * 12,
      annualInsurance: monthlyInsurance * 12
    };
    result.totalAnnual += result.ceo.annualSalary + result.ceo.annualInsurance;
  }
  
  // 일반 센터장 계산 (모든 보험 + 퇴직금)
  if (directorCount > 0 && directorSalary > 0) {
    const totalDirectorSalary = directorSalary * directorCount;
    const nationalPension = Math.round(totalDirectorSalary * (props.taxData?.tr_yeon || 0));
    const healthInsurance = Math.round(totalDirectorSalary * (props.taxData?.tr_gun || 0));
    const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
    const employmentInsurance = Math.round(totalDirectorSalary * (props.taxData?.tr_go || 0));
    const unemployment = Math.round(totalDirectorSalary * (props.taxData?.tr_sil || 0));
    const industrialAccident = Math.round(totalDirectorSalary * (props.taxData?.tr_san || 0));
    const retirement = Math.round(totalDirectorSalary / 12);
    const monthlyInsurance = nationalPension + healthInsurance + longTermCare + employmentInsurance + unemployment + industrialAccident + retirement;
    
    result.director = {
      annualSalary: totalDirectorSalary * 12,
      annualInsurance: monthlyInsurance * 12
    };
    result.totalAnnual += result.director.annualSalary + result.director.annualInsurance;
  }
  
  if (result.totalAnnual === 0) {
    return null;
  }
  
  return result;
});

// 목표 수급자수 가져오기 (Q1에서 입력한 값)
const targetClientsCount = computed(() => {
  const answer = answers.value['revenue_target_clients'];
  if (answer) {
    return parseNumber(String(answer)) || 0;
  }
  return 0;
});

// 요양보호사 시급 기반 월급 계산 미리보기
const caregiverSalaryPreview = computed(() => {
  const question = currentQuestion.value;
  
  if (!question || question.inputType !== 'caregiver-salary') {
    return null;
  }
  
  const hourlyWage = Number(compositeAnswers.value['hourlyWage']) || 0;
  const count = targetClientsCount.value; // 요양보호사 수 = 수급자 수 (자동)
  
  if (hourlyWage <= 0 || count <= 0) {
    return null;
  }
  
  // 월급 = 시급 × 60시간 (180분 × 20회 = 3600분 = 60시간)
  const monthlySalary = hourlyWage * 60;
  const totalMonthlySalary = monthlySalary * count;
  
  return {
    hourlyWage,
    count,
    monthlySalary,
    totalMonthlySalary
  };
});

// 요양보호사 사회보험부담금 미리보기 계산
// 요양보호사 사회보험부담금 미리보기 계산 (대표자/일반 구분)
const caregiverInsurancePreview = computed(() => {
  const question = currentQuestion.value;
  
  if (!question || question.inputType !== 'caregiver-salary') {
    return null;
  }
  
  const hasCeo = compositeAnswers.value['hasCeo'];
  const ceoHourlyWage = Number(compositeAnswers.value['ceoHourlyWage']) || 0;
  const ceoCount = Number(compositeAnswers.value['ceoCount']) || 0;
  const caregiverCount = Number(compositeAnswers.value['caregiverCount']) || targetClientsCount.value;
  const caregiverHourlyWage = Number(compositeAnswers.value['caregiverHourlyWage']) || 0;
  
  let result: {
    ceo?: { annualSalary: number; annualInsurance: number };
    caregiver?: { annualSalary: number; annualInsurance: number };
    totalAnnual: number;
  } = { totalAnnual: 0 };
  
  // 대표자 계산 (고용/산재/퇴직금 제외)
  if (hasCeo && ceoHourlyWage > 0 && ceoCount > 0) {
    const monthlySalary = ceoHourlyWage * 60;
    const totalCeoSalary = monthlySalary * ceoCount;
    const nationalPension = Math.round(totalCeoSalary * (props.taxData?.tr_yeon || 0));
    const healthInsurance = Math.round(totalCeoSalary * (props.taxData?.tr_gun || 0));
    const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
    const monthlyInsurance = nationalPension + healthInsurance + longTermCare;
    
    result.ceo = {
      annualSalary: totalCeoSalary * 12,
      annualInsurance: monthlyInsurance * 12
    };
    result.totalAnnual += result.ceo.annualSalary + result.ceo.annualInsurance;
  }
  
  // 일반 요양보호사 계산 (모든 보험 + 퇴직금)
  if (caregiverCount > 0 && caregiverHourlyWage > 0) {
    const monthlySalary = caregiverHourlyWage * 60;
    const totalCaregiverSalary = monthlySalary * caregiverCount;
    const nationalPension = Math.round(totalCaregiverSalary * (props.taxData?.tr_yeon || 0));
    const healthInsurance = Math.round(totalCaregiverSalary * (props.taxData?.tr_gun || 0));
    const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
    const employmentInsurance = Math.round(totalCaregiverSalary * (props.taxData?.tr_go || 0));
    const unemployment = Math.round(totalCaregiverSalary * (props.taxData?.tr_sil || 0));
    const industrialAccident = Math.round(totalCaregiverSalary * (props.taxData?.tr_san || 0));
    const retirement = Math.round(totalCaregiverSalary / 12);
    const monthlyInsurance = nationalPension + healthInsurance + longTermCare + employmentInsurance + unemployment + industrialAccident + retirement;
    
    result.caregiver = {
      annualSalary: totalCaregiverSalary * 12,
      annualInsurance: monthlyInsurance * 12
    };
    result.totalAnnual += result.caregiver.annualSalary + result.caregiver.annualInsurance;
  }
  
  if (result.totalAnnual === 0) {
    return null;
  }
  
  return result;
});

// 사회복지사 가산금 미리보기 계산 (세입)
const socialWorkerBonusPreview = computed(() => {
  const question = currentQuestion.value;
  
  if (!question || question.inputType !== 'social-worker-salary') {
    return null;
  }
  
  // 전체 사회복지사 수 = 대표자 수 + 일반 사회복지사 수
  const hasCeo = compositeAnswers.value['hasCeo'];
  const ceoCount = hasCeo ? (Number(compositeAnswers.value['ceoCount']) || 0) : 0;
  const workerCount = Number(compositeAnswers.value['workerCount']) || 0;
  const totalCount = ceoCount + workerCount;
  
  if (totalCount <= 0) {
    return null;
  }
  
  // 180분 수가 데이터 가져오기
  const suga = props.sugaList?.find((s: SugaData) => s.suga_rank === '180분');
  if (!suga) {
    return null;
  }
  
  const sugaMoney = Number(suga.suga_money) || 0;
  const targetClients = targetClientsCount.value || 0;
  
  if (targetClients <= 0) {
    return null;
  }
  
  // 수급자별 청구금액 = 180분 수가 × 20회
  const clientBillingAmount = sugaMoney * 20;
  
  // 가산점수
  const bonusPoint = getSocialWorkerBonusPoint(totalCount);
  
  // 가산금 = (수급자별 청구금액 × 85% ÷ 청구인원수) × 가산점수 × 1.8
  const monthlyBonus = Math.round((clientBillingAmount * 0.85 / targetClients) * bonusPoint * 1.8 * targetClients);
  const annualBonus = monthlyBonus * 12;
  
  return {
    count: totalCount,
    bonusPoint,
    clientBillingAmount,
    monthlyBonus,
    annualBonus
  };
});

// 사회복지사 사회보험부담금 미리보기 계산 (대표자/일반 구분)
const socialWorkerInsurancePreview = computed(() => {
  const question = currentQuestion.value;
  
  if (!question || question.inputType !== 'social-worker-salary') {
    return null;
  }
  
  const hasCeo = compositeAnswers.value['hasCeo'];
  const ceoSalary = Number(compositeAnswers.value['ceoSalary']) || 0;
  const ceoCount = Number(compositeAnswers.value['ceoCount']) || 0;
  const workerCount = Number(compositeAnswers.value['workerCount']) || 0;
  const workerSalary = Number(compositeAnswers.value['workerSalary']) || 0;
  
  let result: {
    ceo?: { annualSalary: number; annualInsurance: number };
    worker?: { annualSalary: number; annualInsurance: number };
    totalAnnual: number;
  } = { totalAnnual: 0 };
  
  // 대표자 계산 (고용/산재/퇴직금 제외)
  if (hasCeo && ceoSalary > 0 && ceoCount > 0) {
    const totalCeoSalary = ceoSalary * ceoCount;
    const nationalPension = Math.round(totalCeoSalary * (props.taxData?.tr_yeon || 0));
    const healthInsurance = Math.round(totalCeoSalary * (props.taxData?.tr_gun || 0));
    const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
    const monthlyInsurance = nationalPension + healthInsurance + longTermCare;
    
    result.ceo = {
      annualSalary: totalCeoSalary * 12,
      annualInsurance: monthlyInsurance * 12
    };
    result.totalAnnual += result.ceo.annualSalary + result.ceo.annualInsurance;
  }
  
  // 일반 사회복지사 계산 (모든 보험 + 퇴직금)
  if (workerCount > 0 && workerSalary > 0) {
    const totalWorkerSalary = workerSalary * workerCount;
    const nationalPension = Math.round(totalWorkerSalary * (props.taxData?.tr_yeon || 0));
    const healthInsurance = Math.round(totalWorkerSalary * (props.taxData?.tr_gun || 0));
    const longTermCare = Math.round(healthInsurance * (props.taxData?.tr_jang || 0));
    const employmentInsurance = Math.round(totalWorkerSalary * (props.taxData?.tr_go || 0));
    const unemployment = Math.round(totalWorkerSalary * (props.taxData?.tr_sil || 0));
    const industrialAccident = Math.round(totalWorkerSalary * (props.taxData?.tr_san || 0));
    const retirement = Math.round(totalWorkerSalary / 12);
    const monthlyInsurance = nationalPension + healthInsurance + longTermCare + employmentInsurance + unemployment + industrialAccident + retirement;
    
    result.worker = {
      annualSalary: totalWorkerSalary * 12,
      annualInsurance: monthlyInsurance * 12
    };
    result.totalAnnual += result.worker.annualSalary + result.worker.annualInsurance;
  }
  
  if (result.totalAnnual === 0) {
    return null;
  }
  
  return result;
});

// ============================================
// 세출 계산을 위한 유틸리티 함수들
// ============================================

// 소득세 계산 함수
const calculateIncomeTax = (taxableIncome: number): number => {
  if (!props.sojiData || props.sojiData.length === 0) {
    return 0;
  }
  
  for (let i = 0; i < props.sojiData.length; i++) {
    const obj = props.sojiData[i];
    const minIncome = obj.income1 * 1000;
    const maxIncome = obj.income2 * 1000;
    
    if (taxableIncome >= minIncome && taxableIncome < maxIncome) {
      return Math.round(obj.depe_1);
    }
  }
  
  return 0;
};

// 근로자 4대보험 공제 계산 (급여 기준)
const calculateEmployeeDeductions = (salary: number) => {
  if (!props.taxData || !props.taxData.tr_time_pay) {
    return {
      nationalPension: 0,      // 국민연금
      healthInsurance: 0,      // 건강보험
      longTermCare: 0,         // 장기요양보험
      employmentInsurance: 0,  // 고용보험
      incomeTax: 0,            // 소득세
      localTax: 0,             // 지방세
      total: 0
    };
  }
  
  const nationalPension = Math.round(salary * (props.taxData.tr_yeon || 0));
  const healthInsurance = Math.round(salary * (props.taxData.tr_gun || 0));
  const longTermCare = Math.round(healthInsurance * (props.taxData.tr_jang || 0));
  const employmentInsurance = Math.round(salary * (props.taxData.tr_go || 0));
  const incomeTax = calculateIncomeTax(salary);
  const localTax = Math.round(incomeTax * 0.1);
  
  return {
    nationalPension,
    healthInsurance,
    longTermCare,
    employmentInsurance,
    incomeTax,
    localTax,
    total: nationalPension + healthInsurance + longTermCare + employmentInsurance + incomeTax + localTax
  };
};

// 사업자 부담 4대보험 계산 (급여 기준)
const calculateEmployerDeductions = (salary: number) => {
  if (!props.taxData || !props.taxData.tr_time_pay) {
    return {
      nationalPension: 0,      // 국민연금
      healthInsurance: 0,      // 건강보험
      longTermCare: 0,         // 장기요양보험
      employmentInsurance: 0,  // 고용보험
      unemployment: 0,         // 실업급여
      industrialAccident: 0,   // 산재보험
      retirement: 0,           // 퇴직금
      total: 0
    };
  }
  
  const nationalPension = Math.round(salary * (props.taxData.tr_yeon || 0));
  const healthInsurance = Math.round(salary * (props.taxData.tr_gun || 0));
  const longTermCare = Math.round(healthInsurance * (props.taxData.tr_jang || 0));
  const employmentInsurance = Math.round(salary * (props.taxData.tr_go || 0));
  const unemployment = Math.round(salary * (props.taxData.tr_sil || 0));
  const industrialAccident = Math.round(salary * (props.taxData.tr_san || 0));
  const retirement = Math.round(salary / 12); // 퇴직금 = 월급 / 12
  
  return {
    nationalPension,
    healthInsurance,
    longTermCare,
    employmentInsurance,
    unemployment,
    industrialAccident,
    retirement,
    total: nationalPension + healthInsurance + longTermCare + employmentInsurance + unemployment + industrialAccident + retirement
  };
};

// 연간 사회보험부담금 계산 (사업자 부담분, 12개월)
const calculateAnnualEmployerInsurance = (monthlySalary: number): number => {
  const deductions = calculateEmployerDeductions(monthlySalary);
  // 퇴직금 제외한 보험료 합계 × 12개월
  const monthlyInsurance = deductions.nationalPension + 
                          deductions.healthInsurance + 
                          deductions.longTermCare + 
                          deductions.employmentInsurance + 
                          deductions.unemployment + 
                          deductions.industrialAccident;
  return monthlyInsurance * 12;
};

// 연간 퇴직금 계산 (12개월)
const calculateAnnualRetirement = (monthlySalary: number): number => {
  // 퇴직금 = 월급 / 12 × 12개월 = 월급
  return monthlySalary;
};

// 최저시급 가져오기
const minimumWage = computed(() => {
  return props.taxData?.tr_time_pay || 0;
});

// 세금/보험 데이터 유효 여부
const hasTaxData = computed(() => {
  return props.taxData && props.taxData.tr_time_pay && props.taxData.tr_time_pay > 0;
});

// 설명 파싱 (번호가 있으면 리스트로 변환)
const parsedDescription = computed(() => {
  const desc = currentQuestion.value?.description || '';
  
  // ①②③ 또는 1. 2. 3. 형태의 번호 패턴 감지
  const circledNumberPattern = /[①②③④⑤⑥⑦⑧⑨⑩]/g;
  const numberedPattern = /(\d+)\.\s/g;
  
  const hasCircledNumbers = circledNumberPattern.test(desc);
  const hasNumberedList = numberedPattern.test(desc);
  
  if (hasCircledNumbers) {
    // ① ② ③ 형식 파싱
    const items: { number: string; text: string }[] = [];
    const parts = desc.split(/([①②③④⑤⑥⑦⑧⑨⑩])/);
    
    for (let i = 1; i < parts.length; i += 2) {
      const number = parts[i];
      const text = parts[i + 1]?.trim() || '';
      if (text) {
        items.push({ number, text });
      }
    }
    
    return { isList: items.length > 0, items };
  }
  
  if (hasNumberedList) {
    // 1. 2. 3. 형식 파싱
    const items: { number: string; text: string }[] = [];
    const regex = /(\d+)\.\s([^0-9]+?)(?=\d+\.|$)/g;
    let match;
    
    while ((match = regex.exec(desc)) !== null) {
      items.push({ 
        number: match[1] + '.', 
        text: match[2].trim() 
      });
    }
    
    return { isList: items.length > 0, items };
  }
  
  return { isList: false, items: [] };
});

// Methods
const parseNumber = (value: string): number => {
  if (!value) return 0;
  return Number(value.replace(/[^0-9.-]/g, '')) || 0;
};

const formatMoney = (amount: number): string => {
  return new Intl.NumberFormat('ko-KR').format(amount);
};

const handleNumberInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = target.value.replace(/[^0-9]/g, '');
  currentAnswer.value = value ? Number(value).toLocaleString() : '';
};

// multi-number 입력 값 포맷팅
const formatMultiNumberValue = (key: string): string => {
  const value = compositeAnswers.value[key];
  if (!value) return '';
  return Number(value).toLocaleString();
};

// multi-number 입력 핸들러
const handleMultiNumberInput = (event: Event, key: string) => {
  const target = event.target as HTMLInputElement;
  const value = target.value.replace(/[^0-9]/g, '');
  compositeAnswers.value[key] = value ? Number(value) : '';
};

const handleEnterKey = () => {
  if (isCurrentAnswerValid.value) {
    // 현재 답변 먼저 저장
    saveCurrentAnswer();
    
    if (currentStep.value < totalQuestions.value) {
      nextQuestion();
    } else {
      // 마지막 질문이면 바로 제출 (saveCurrentAnswer 후이므로 isAllAnswered 체크 불필요)
      submitBudget();
    }
  }
};

const saveCurrentAnswer = () => {
  const question = currentQuestion.value;
  if (!question) return;
  
  if (question.inputType === 'composite' || question.inputType === 'multi-number' || question.inputType === 'salary' || question.inputType === 'caregiver-salary' || question.inputType === 'social-worker-salary' || question.inputType === 'director-salary') {
    answers.value[question.code] = JSON.stringify(compositeAnswers.value);
  } else {
    answers.value[question.code] = currentAnswer.value || question.defaultValue || '';
  }
};

const loadCurrentAnswer = () => {
  const question = currentQuestion.value;
  if (!question) return;
  
  if (question.inputType === 'composite' || question.inputType === 'multi-number' || question.inputType === 'salary' || question.inputType === 'caregiver-salary' || question.inputType === 'social-worker-salary' || question.inputType === 'director-salary') {
    const saved = answers.value[question.code];
    if (saved && typeof saved === 'string') {
      try {
        const parsed = JSON.parse(saved);
        compositeAnswers.value = { ...parsed };
      } catch {
        compositeAnswers.value = {};
      }
    } else {
      compositeAnswers.value = {};
      // caregiver-salary의 경우 기본값 설정
      if (question.inputType === 'caregiver-salary') {
        compositeAnswers.value['hasCeo'] = false;
        compositeAnswers.value['ceoCount'] = 1;
        compositeAnswers.value['caregiverCount'] = targetClientsCount.value; // 수급자 수 기본값
      }
      // social-worker-salary의 경우 기본값 설정
      if (question.inputType === 'social-worker-salary') {
        compositeAnswers.value['hasCeo'] = false;
        compositeAnswers.value['ceoCount'] = 1;
        compositeAnswers.value['workerCount'] = 0;
      }
      // director-salary의 경우 대표자 수 기본값 1명 설정
      if (question.inputType === 'director-salary') {
        compositeAnswers.value['hasCeo'] = false;
        compositeAnswers.value['ceoCount'] = 1;
        compositeAnswers.value['directorCount'] = 0;
      }
      // salary 타입 중 센터장 급여의 경우 센터장 수 기본값 1명 설정 (legacy)
      if (question.inputType === 'salary' && question.code === 'expense_director_salary') {
        compositeAnswers.value['count'] = 1;
      }
    }
    currentAnswer.value = '';
  } else {
    currentAnswer.value = String(answers.value[question.code] || question.defaultValue || '');
  }
};

// 첫 번째 입력 필드에 포커스
const focusFirstInput = () => {
  // transition 애니메이션 완료 후 포커스 (약간의 딜레이 필요)
  setTimeout(() => {
    nextTick(() => {
      const questionCard = document.querySelector('.question-card');
      if (questionCard) {
        // .main-input 또는 .main-select 클래스를 가진 첫 번째 활성화된 요소 찾기
        // (v-switch 내부의 input 제외)
        const firstInput = questionCard.querySelector('.main-input:not([disabled]), .main-select:not([disabled])') as HTMLInputElement | HTMLSelectElement | null;
        if (firstInput) {
          firstInput.focus();
        }
      }
    });
  }, 150); // transition 시간 고려
};

const prevQuestion = () => {
  if (currentStep.value > 1) {
    saveCurrentAnswer();
    currentStep.value--;
    loadCurrentAnswer();
    // transition 완료 후 첫 번째 입력 필드에 포커스
    focusFirstInput();
  }
};

const nextQuestion = () => {
  if (currentStep.value < totalQuestions.value) {
    saveCurrentAnswer();
    currentStep.value++;
    loadCurrentAnswer();
    // transition 완료 후 첫 번째 입력 필드에 포커스
    focusFirstInput();
  }
};

const goToStep = (step: number) => {
  saveCurrentAnswer();
  currentStep.value = step;
  loadCurrentAnswer();
};

const goToQuestion = (code: string) => {
  const index = allQuestions.value.findIndex(q => q.code === code);
  if (index >= 0) {
    goToStep(index + 1);
  }
};

const goToSection = (section: 'revenue' | 'expense') => {
  const questions = section === 'revenue' ? revenueQuestions.value : expenseQuestions.value;
  const firstQuestion = questions[0];
  if (firstQuestion) {
    goToQuestion(firstQuestion.code);
  }
};

// props의 수가 데이터로 질문 옵션 업데이트 (필요시)
const updateSugaOptions = () => {
  const serviceQuestion = revenueQuestions.value.find(q => q.code === 'revenue_service_standard');
  if (serviceQuestion && serviceQuestion.fields && props.sugaList) {
    const timeField = serviceQuestion.fields.find(f => f.key === 'serviceTime');
    if (timeField) {
      timeField.options = props.sugaList.map((suga: SugaData) => ({
        value: suga.suga_rank,
        label: `${suga.suga_rank}분 (${formatMoney(suga.suga_money)}원)`
      }));
    }
  }
};

const submitBudget = () => {
  saveCurrentAnswer();
  
  const budgetItems: BudgetItem[] = [];
  const generatedEntries: HhBudgetEntry[] = [];
  
  const sisulCode = store.state.user?.m_sisul_code || '';
  const year = searchParams.value.lebYear;
  
  // 180분 수가 데이터
  const suga = props.sugaList?.find(s => s.suga_rank === '180분');
  const sugaMoney = suga ? Number(suga.suga_money) : 0;
  const sugaBonin = suga ? Number(suga.suga_bonin) : 0;
  const sugaGubyo = sugaMoney - sugaBonin; // 급여비용 (공단부담)
  
  // 세입 항목 처리
  revenueQuestions.value.forEach(q => {
    const value = parseNumber(String(answers.value[q.code])) || 0;
    let monthly = 0;
    let annual = 0;
    
    // 목표 수급자수 → 본인부담금수입(112) + 장기요양급여수입(611) 두 개 생성
    if (q.code === 'revenue_target_clients') {
      const targetClients = value;
      if (targetClients > 0 && suga) {
        // 1. 본인부담금수입 (hb_scode: 112)
        const boninPrice = sugaBonin * 20; // 180분수가 본인부담금 × 20회
        const boninAmount = boninPrice * targetClients * 12;
        
        generatedEntries.push({
          m_sisul_code: sisulCode,
          hb_year: year,
          hb_gubun: 'IN',
          hb_scode: '112',
          hb_bud_class: '',
          hb_memo: '180분수가 X 20',
          hb_price: String(boninPrice),
          hb_person: String(targetClients),
          hb_month: '12',
          hb_amount: String(boninAmount),
          hb_funding: '06', // 이용료
        });
        
        budgetItems.push({
          code: 'revenue_bonin_112',
          section: '세입',
          label: '본인부담금수입',
          monthly: Math.round(boninAmount / 12),
          annual: boninAmount
        });
        
        // 2. 장기요양급여수입 (hb_scode: 611)
        const gubyoPrice = sugaGubyo * 20; // (수가 - 본인부담) × 20회
        const gubyoAmount = gubyoPrice * targetClients * 12;
        
        generatedEntries.push({
          m_sisul_code: sisulCode,
          hb_year: year,
          hb_gubun: 'IN',
          hb_scode: '611',
          hb_bud_class: '',
          hb_memo: '180분수가 X 20',
          hb_price: String(gubyoPrice),
          hb_person: String(targetClients),
          hb_month: '12',
          hb_amount: String(gubyoAmount),
          hb_funding: '06', // 이용료
        });
        
        budgetItems.push({
          code: 'revenue_gubyo_611',
          section: '세입',
          label: '장기요양급여수입',
          monthly: Math.round(gubyoAmount / 12),
          annual: gubyoAmount
        });
        
        // 전체 합계용
        monthly = Math.round((boninAmount + gubyoAmount) / 12);
        annual = boninAmount + gubyoAmount;
      }
    }
    // multi-number: 각 필드별로 별도 hh_budget 엔트리 생성
    else if (q.inputType === 'multi-number' && q.fields) {
      const savedAnswer = answers.value[q.code];
      if (savedAnswer && typeof savedAnswer === 'string') {
        try {
          const parsed = JSON.parse(savedAnswer);
          q.fields.forEach(field => {
            const fieldValue = Number(parsed[field.key]) || 0;
            if (fieldValue > 0 && field.scode) {
              // hb_price = 입력값, hb_person = 1, hb_month = 1, hb_amount = price * 1 * 1
              generatedEntries.push({
                m_sisul_code: sisulCode,
                hb_year: year,
                hb_gubun: 'IN',
                hb_scode: field.scode,
                hb_bud_class: '',
                hb_memo: field.label,
                hb_price: String(fieldValue),
                hb_person: '1',
                hb_month: '1',
                hb_amount: String(fieldValue),
                hb_funding: field.funding || '04',
              });
              
              budgetItems.push({
                code: `${q.code}_${field.key}`,
                section: q.section,
                label: field.label,
                monthly: Math.round(fieldValue / 12),
                annual: fieldValue
              });
            }
          });
        } catch { /* ignore */ }
      }
    }
    else if (q.frequency === 'monthly') {
      monthly = value;
      annual = value * 12;
      
      budgetItems.push({
        code: q.code,
        section: q.section,
        label: q.shortLabel || q.question,
        monthly,
        annual
      });
    } else if (q.frequency === 'once') {
      monthly = Math.round(value / 12);
      annual = value;
      
      budgetItems.push({
        code: q.code,
        section: q.section,
        label: q.shortLabel || q.question,
        monthly,
        annual
      });
    }
  });
  
  // 세출 항목 처리
  expenseQuestions.value.forEach(q => {
    // reference(참고용)는 제외
    if (q.frequency === 'reference') return;
    
    // multi-number 타입: 각 필드별로 별도 저장
    if (q.inputType === 'multi-number' && q.fields) {
      const savedAnswer = answers.value[q.code];
      if (savedAnswer && typeof savedAnswer === 'string') {
        try {
          const parsed = JSON.parse(savedAnswer);
          q.fields.forEach(field => {
            const fieldValue = Number(parsed[field.key]) || 0;
            if (fieldValue > 0) {
              const annualAmount = fieldValue * 12; // 월 금액 × 12개월
              
              generatedEntries.push({
                m_sisul_code: sisulCode,
                hb_year: year,
                hb_gubun: 'OUT',
                hb_scode: field.scode || '',
                hb_bud_class: '',
                hb_memo: field.label,
                hb_price: String(fieldValue),
                hb_person: '1',
                hb_month: '12',
                hb_amount: String(annualAmount),
                hb_funding: field.funding || '',
              });
              
              budgetItems.push({
                code: `${q.code}_${field.key}`,
                section: q.section,
                label: field.label,
                monthly: fieldValue,
                annual: annualAmount
              });
            }
          });
        } catch { /* ignore */ }
      }
      return;
    }
    
    // salary 타입: 급여 + 사회보험부담금 별도 저장
    if (q.inputType === 'salary') {
      const savedAnswer = answers.value[q.code];
      if (savedAnswer && typeof savedAnswer === 'string') {
        try {
          const parsed = JSON.parse(savedAnswer);
          const salary = Number(parsed['salary']) || 0;
          const count = Number(parsed['count']) || 1;
          
          if (salary > 0) {
            const totalSalary = salary * count;
            const annualSalary = totalSalary * 12;
            
            // 급여-보험-퇴직금 연결 그룹 ID (질문 코드 기반)
            const payRelGroup = `salary_${q.code}`;
            
            // 1. 급여 저장
            generatedEntries.push({
              m_sisul_code: sisulCode,
              hb_year: year,
              hb_gubun: 'OUT',
              hb_scode: q.scode || '1112',
              hb_bud_class: '',
              hb_memo: q.shortLabel || '급여',
              hb_price: String(salary),
              hb_person: String(count),
              hb_month: '12',
              hb_amount: String(annualSalary),
              hb_funding: q.funding || '',
              pay_rel_group: payRelGroup,
              pay_rel_type: 'salary',
            });
            
            budgetItems.push({
              code: q.code,
              section: q.section,
              label: q.shortLabel || q.question,
              monthly: totalSalary,
              annual: annualSalary
            });
            
            // 2. 사회보험부담금 계산 및 저장 (간접비: 1152)
            const config = q.insuranceConfig || {};
            // 1인당 기준으로 계산
            const nationalPension = config.nationalPension ? Math.round(salary * (props.taxData?.tr_yeon || 0)) : 0;
            const healthInsurance = config.healthInsurance ? Math.round(salary * (props.taxData?.tr_gun || 0)) : 0;
            const longTermCare = config.longTermCare ? Math.round(healthInsurance * (props.taxData?.tr_jang || 0)) : 0;
            const employmentInsurance = config.employmentInsurance ? Math.round(salary * (props.taxData?.tr_go || 0)) : 0;
            const unemployment = config.unemployment ? Math.round(salary * (props.taxData?.tr_sil || 0)) : 0;
            const industrialAccident = config.industrialAccident ? Math.round(salary * (props.taxData?.tr_san || 0)) : 0;
            
            // 1인당 월 사회보험부담금
            const perPersonInsurance = nationalPension + healthInsurance + longTermCare + employmentInsurance + unemployment + industrialAccident;
            // 정확한 총액 = 월 총 급여 기준 계산 × 12
            const totalMonthlyInsurance = (config.nationalPension ? Math.round(totalSalary * (props.taxData?.tr_yeon || 0)) : 0) +
                                          (config.healthInsurance ? Math.round(totalSalary * (props.taxData?.tr_gun || 0)) : 0) +
                                          (config.longTermCare ? Math.round(Math.round(totalSalary * (props.taxData?.tr_gun || 0)) * (props.taxData?.tr_jang || 0)) : 0) +
                                          (config.employmentInsurance ? Math.round(totalSalary * (props.taxData?.tr_go || 0)) : 0) +
                                          (config.unemployment ? Math.round(totalSalary * (props.taxData?.tr_sil || 0)) : 0) +
                                          (config.industrialAccident ? Math.round(totalSalary * (props.taxData?.tr_san || 0)) : 0);
            const annualInsurance = totalMonthlyInsurance * 12;
            
            if (annualInsurance > 0) {
              generatedEntries.push({
                m_sisul_code: sisulCode,
                hb_year: year,
                hb_gubun: 'OUT',
                hb_scode: '1152', // 사회보험부담금(간접비)
                hb_bud_class: '',
                hb_memo: `${q.shortLabel || '급여'} 사회보험부담금`,
                hb_price: String(perPersonInsurance),
                hb_person: String(count),
                hb_month: '12',
                hb_amount: String(annualInsurance),
                hb_funding: '',
                pay_rel_group: payRelGroup,
                pay_rel_type: 'insurance',
              });
              
              budgetItems.push({
                code: `${q.code}_insurance`,
                section: q.section,
                label: `${q.shortLabel} 사회보험부담금`,
                monthly: totalMonthlyInsurance,
                annual: annualInsurance
              });
            }
            
            // 3. 퇴직금 계산 및 저장 (간접비: 1142)
            if (config.retirement) {
              // 1인당 월 퇴직금
              const perPersonRetirement = Math.round(salary / 12);
              // 정확한 총액 = (월 총 급여 × 12) / 12 = 월 총 급여
              const annualRetirement = totalSalary * 12 / 12; // = totalSalary
              
              generatedEntries.push({
                m_sisul_code: sisulCode,
                hb_year: year,
                hb_gubun: 'OUT',
                hb_scode: '1142', // 퇴직금(간접비)
                hb_bud_class: '',
                hb_memo: `${q.shortLabel || '급여'} 퇴직금`,
                hb_price: String(perPersonRetirement),
                hb_person: String(count),
                hb_month: '12',
                hb_amount: String(annualRetirement),
                hb_funding: '',
                pay_rel_group: payRelGroup,
                pay_rel_type: 'retirement',
              });
              
              budgetItems.push({
                code: `${q.code}_retirement`,
                section: q.section,
                label: `${q.shortLabel} 퇴직금`,
                monthly: Math.round(annualRetirement / 12),
                annual: annualRetirement
              });
            }
          }
        } catch { /* ignore */ }
      }
      return;
    }
    
    // director-salary 타입: 대표자/일반 센터장 구분하여 저장
    if (q.inputType === 'director-salary') {
      const savedAnswer = answers.value[q.code];
      if (savedAnswer && typeof savedAnswer === 'string') {
        try {
          const parsed = JSON.parse(savedAnswer);
          const hasCeo = parsed['hasCeo'];
          const ceoSalary = Number(parsed['ceoSalary']) || 0;
          const ceoCount = Number(parsed['ceoCount']) || 0;
          const directorCount = Number(parsed['directorCount']) || 0;
          const directorSalary = Number(parsed['directorSalary']) || 0;
          
          // 대표자 저장 (고용/산재/퇴직금 제외)
          if (hasCeo && ceoSalary > 0 && ceoCount > 0) {
            const totalCeoSalary = ceoSalary * ceoCount;
            const annualCeoSalary = totalCeoSalary * 12;
            const payRelGroup = `salary_${q.code}_ceo`;
            
            // 대표자 급여
            generatedEntries.push({
              m_sisul_code: sisulCode,
              hb_year: year,
              hb_gubun: 'OUT',
              hb_scode: q.scode || '1112',
              hb_bud_class: '',
              hb_memo: '센터장(대표자) 급여',
              hb_price: String(ceoSalary),
              hb_person: String(ceoCount),
              hb_month: '12',
              hb_amount: String(annualCeoSalary),
              hb_funding: q.funding || '',
              pay_rel_group: payRelGroup,
              pay_rel_type: 'salary',
            });
            
            budgetItems.push({
              code: `${q.code}_ceo`,
              section: q.section,
              label: '센터장(대표자) 급여',
              monthly: totalCeoSalary,
              annual: annualCeoSalary
            });
            
            // 대표자 사회보험부담금 (국민연금, 건강보험, 장기요양만) - 1인당 기준
            const perPersonNationalPension = Math.round(ceoSalary * (props.taxData?.tr_yeon || 0));
            const perPersonHealthInsurance = Math.round(ceoSalary * (props.taxData?.tr_gun || 0));
            const perPersonLongTermCare = Math.round(perPersonHealthInsurance * (props.taxData?.tr_jang || 0));
            const perPersonInsurance = perPersonNationalPension + perPersonHealthInsurance + perPersonLongTermCare;
            
            // 정확한 총액 (월 총 급여 기준)
            const totalNationalPension = Math.round(totalCeoSalary * (props.taxData?.tr_yeon || 0));
            const totalHealthInsurance = Math.round(totalCeoSalary * (props.taxData?.tr_gun || 0));
            const totalLongTermCare = Math.round(totalHealthInsurance * (props.taxData?.tr_jang || 0));
            const monthlyInsurance = totalNationalPension + totalHealthInsurance + totalLongTermCare;
            const annualInsurance = monthlyInsurance * 12;
            
            if (annualInsurance > 0) {
              generatedEntries.push({
                m_sisul_code: sisulCode,
                hb_year: year,
                hb_gubun: 'OUT',
                hb_scode: '1152',
                hb_bud_class: '',
                hb_memo: '센터장(대표자) 사회보험부담금',
                hb_price: String(perPersonInsurance),
                hb_person: String(ceoCount),
                hb_month: '12',
                hb_amount: String(annualInsurance),
                hb_funding: '',
                pay_rel_group: payRelGroup,
                pay_rel_type: 'insurance',
              });
              
              budgetItems.push({
                code: `${q.code}_ceo_insurance`,
                section: q.section,
                label: '센터장(대표자) 사회보험부담금',
                monthly: monthlyInsurance,
                annual: annualInsurance
              });
            }
          }
          
          // 일반 센터장 저장 (모든 보험 + 퇴직금)
          if (directorCount > 0 && directorSalary > 0) {
            const totalDirectorSalary = directorSalary * directorCount;
            const annualDirectorSalary = totalDirectorSalary * 12;
            const payRelGroup = `salary_${q.code}_director`;
            
            // 일반 센터장 급여
            generatedEntries.push({
              m_sisul_code: sisulCode,
              hb_year: year,
              hb_gubun: 'OUT',
              hb_scode: q.scode || '1112',
              hb_bud_class: '',
              hb_memo: '센터장 급여',
              hb_price: String(directorSalary),
              hb_person: String(directorCount),
              hb_month: '12',
              hb_amount: String(annualDirectorSalary),
              hb_funding: q.funding || '',
              pay_rel_group: payRelGroup,
              pay_rel_type: 'salary',
            });
            
            budgetItems.push({
              code: `${q.code}_director`,
              section: q.section,
              label: '센터장 급여',
              monthly: totalDirectorSalary,
              annual: annualDirectorSalary
            });
            
            // 일반 센터장 사회보험부담금 (1인당 기준)
            const perPersonNationalPension = Math.round(directorSalary * (props.taxData?.tr_yeon || 0));
            const perPersonHealthInsurance = Math.round(directorSalary * (props.taxData?.tr_gun || 0));
            const perPersonLongTermCare = Math.round(perPersonHealthInsurance * (props.taxData?.tr_jang || 0));
            const perPersonEmploymentInsurance = Math.round(directorSalary * (props.taxData?.tr_go || 0));
            const perPersonUnemployment = Math.round(directorSalary * (props.taxData?.tr_sil || 0));
            const perPersonIndustrialAccident = Math.round(directorSalary * (props.taxData?.tr_san || 0));
            const perPersonInsurance = perPersonNationalPension + perPersonHealthInsurance + perPersonLongTermCare + perPersonEmploymentInsurance + perPersonUnemployment + perPersonIndustrialAccident;
            
            // 정확한 총액 (월 총 급여 기준)
            const totalNationalPension = Math.round(totalDirectorSalary * (props.taxData?.tr_yeon || 0));
            const totalHealthInsurance = Math.round(totalDirectorSalary * (props.taxData?.tr_gun || 0));
            const totalLongTermCare = Math.round(totalHealthInsurance * (props.taxData?.tr_jang || 0));
            const totalEmploymentInsurance = Math.round(totalDirectorSalary * (props.taxData?.tr_go || 0));
            const totalUnemployment = Math.round(totalDirectorSalary * (props.taxData?.tr_sil || 0));
            const totalIndustrialAccident = Math.round(totalDirectorSalary * (props.taxData?.tr_san || 0));
            const monthlyInsurance = totalNationalPension + totalHealthInsurance + totalLongTermCare + totalEmploymentInsurance + totalUnemployment + totalIndustrialAccident;
            const annualInsurance = monthlyInsurance * 12;
            
            if (annualInsurance > 0) {
              generatedEntries.push({
                m_sisul_code: sisulCode,
                hb_year: year,
                hb_gubun: 'OUT',
                hb_scode: '1152',
                hb_bud_class: '',
                hb_memo: '센터장 사회보험부담금',
                hb_price: String(perPersonInsurance),
                hb_person: String(directorCount),
                hb_month: '12',
                hb_amount: String(annualInsurance),
                hb_funding: '',
                pay_rel_group: payRelGroup,
                pay_rel_type: 'insurance',
              });
              
              budgetItems.push({
                code: `${q.code}_director_insurance`,
                section: q.section,
                label: '센터장 사회보험부담금',
                monthly: monthlyInsurance,
                annual: annualInsurance
              });
            }
            
            // 일반 센터장 퇴직금 (1인당 기준)
            const perPersonRetirement = Math.round(directorSalary / 12);
            // 정확한 총액 = 월 총 급여
            const annualRetirement = totalDirectorSalary;
            
            generatedEntries.push({
              m_sisul_code: sisulCode,
              hb_year: year,
              hb_gubun: 'OUT',
              hb_scode: '1142',
              hb_bud_class: '',
              hb_memo: '센터장 퇴직금',
              hb_price: String(perPersonRetirement),
              hb_person: String(directorCount),
              hb_month: '12',
              hb_amount: String(annualRetirement),
              hb_funding: '',
              pay_rel_group: payRelGroup,
              pay_rel_type: 'retirement',
            });
            
            budgetItems.push({
              code: `${q.code}_director_retirement`,
              section: q.section,
              label: '센터장 퇴직금',
              monthly: Math.round(annualRetirement / 12),
              annual: annualRetirement
            });
          }
        } catch { /* ignore */ }
      }
      return;
    }
    
    // caregiver-salary 타입: 대표자/일반 구분하여 저장
    if (q.inputType === 'caregiver-salary') {
      const savedAnswer = answers.value[q.code];
      if (savedAnswer && typeof savedAnswer === 'string') {
        try {
          const parsed = JSON.parse(savedAnswer);
          const hasCeo = parsed['hasCeo'];
          const ceoHourlyWage = Number(parsed['ceoHourlyWage']) || 0;
          const ceoCount = Number(parsed['ceoCount']) || 0;
          const caregiverCount = Number(parsed['caregiverCount']) || targetClientsCount.value;
          const caregiverHourlyWage = Number(parsed['caregiverHourlyWage']) || 0;
          
          // 대표자 저장 (고용/산재/퇴직금 제외)
          if (hasCeo && ceoHourlyWage > 0 && ceoCount > 0) {
            const monthlySalary = ceoHourlyWage * 60;
            const totalCeoSalary = monthlySalary * ceoCount;
            const annualCeoSalary = totalCeoSalary * 12;
            const payRelGroup = `salary_${q.code}_ceo`;
            
            // 대표자 급여
            generatedEntries.push({
              m_sisul_code: sisulCode,
              hb_year: year,
              hb_gubun: 'OUT',
              hb_scode: q.scode || '1111',
              hb_bud_class: '',
              hb_memo: '요양보호사(대표자) 급여',
              hb_price: String(monthlySalary),
              hb_person: String(ceoCount),
              hb_month: '12',
              hb_amount: String(annualCeoSalary),
              hb_funding: q.funding || '06',
              pay_rel_group: payRelGroup,
              pay_rel_type: 'salary',
            });
            
            budgetItems.push({
              code: `${q.code}_ceo`,
              section: q.section,
              label: '요양보호사(대표자) 급여',
              monthly: totalCeoSalary,
              annual: annualCeoSalary
            });
            
            // 대표자 사회보험부담금 (국민연금, 건강보험, 장기요양만) - 1인당 기준
            const perPersonNationalPension = Math.round(monthlySalary * (props.taxData?.tr_yeon || 0));
            const perPersonHealthInsurance = Math.round(monthlySalary * (props.taxData?.tr_gun || 0));
            const perPersonLongTermCare = Math.round(perPersonHealthInsurance * (props.taxData?.tr_jang || 0));
            const perPersonInsurance = perPersonNationalPension + perPersonHealthInsurance + perPersonLongTermCare;
            
            // 정확한 총액 (월 총 급여 기준)
            const totalNationalPension = Math.round(totalCeoSalary * (props.taxData?.tr_yeon || 0));
            const totalHealthInsurance = Math.round(totalCeoSalary * (props.taxData?.tr_gun || 0));
            const totalLongTermCare = Math.round(totalHealthInsurance * (props.taxData?.tr_jang || 0));
            const monthlyInsurance = totalNationalPension + totalHealthInsurance + totalLongTermCare;
            const annualInsurance = monthlyInsurance * 12;
            
            if (annualInsurance > 0) {
              generatedEntries.push({
                m_sisul_code: sisulCode,
                hb_year: year,
                hb_gubun: 'OUT',
                hb_scode: '1151',
                hb_bud_class: '',
                hb_memo: '요양보호사(대표자) 사회보험부담금',
                hb_price: String(perPersonInsurance),
                hb_person: String(ceoCount),
                hb_month: '12',
                hb_amount: String(annualInsurance),
                hb_funding: '06',
                pay_rel_group: payRelGroup,
                pay_rel_type: 'insurance',
              });
              
              budgetItems.push({
                code: `${q.code}_ceo_insurance`,
                section: q.section,
                label: '요양보호사(대표자) 사회보험부담금',
                monthly: monthlyInsurance,
                annual: annualInsurance
              });
            }
          }
          
          // 일반 요양보호사 저장 (모든 보험 + 퇴직금)
          if (caregiverCount > 0 && caregiverHourlyWage > 0) {
            const monthlySalary = caregiverHourlyWage * 60;
            const totalCaregiverSalary = monthlySalary * caregiverCount;
            const annualCaregiverSalary = totalCaregiverSalary * 12;
            const payRelGroup = `salary_${q.code}_caregiver`;
            
            // 일반 요양보호사 급여
            generatedEntries.push({
              m_sisul_code: sisulCode,
              hb_year: year,
              hb_gubun: 'OUT',
              hb_scode: q.scode || '1111',
              hb_bud_class: '',
              hb_memo: '요양보호사 급여',
              hb_price: String(monthlySalary),
              hb_person: String(caregiverCount),
              hb_month: '12',
              hb_amount: String(annualCaregiverSalary),
              hb_funding: q.funding || '06',
              pay_rel_group: payRelGroup,
              pay_rel_type: 'salary',
            });
            
            budgetItems.push({
              code: `${q.code}_caregiver`,
              section: q.section,
              label: '요양보호사 급여',
              monthly: totalCaregiverSalary,
              annual: annualCaregiverSalary
            });
            
            // 일반 요양보호사 사회보험부담금 (1인당 기준)
            const perPersonNationalPension = Math.round(monthlySalary * (props.taxData?.tr_yeon || 0));
            const perPersonHealthInsurance = Math.round(monthlySalary * (props.taxData?.tr_gun || 0));
            const perPersonLongTermCare = Math.round(perPersonHealthInsurance * (props.taxData?.tr_jang || 0));
            const perPersonEmploymentInsurance = Math.round(monthlySalary * (props.taxData?.tr_go || 0));
            const perPersonUnemployment = Math.round(monthlySalary * (props.taxData?.tr_sil || 0));
            const perPersonIndustrialAccident = Math.round(monthlySalary * (props.taxData?.tr_san || 0));
            const perPersonInsurance = perPersonNationalPension + perPersonHealthInsurance + perPersonLongTermCare + perPersonEmploymentInsurance + perPersonUnemployment + perPersonIndustrialAccident;
            
            // 정확한 총액 (월 총 급여 기준)
            const totalNationalPension = Math.round(totalCaregiverSalary * (props.taxData?.tr_yeon || 0));
            const totalHealthInsurance = Math.round(totalCaregiverSalary * (props.taxData?.tr_gun || 0));
            const totalLongTermCare = Math.round(totalHealthInsurance * (props.taxData?.tr_jang || 0));
            const totalEmploymentInsurance = Math.round(totalCaregiverSalary * (props.taxData?.tr_go || 0));
            const totalUnemployment = Math.round(totalCaregiverSalary * (props.taxData?.tr_sil || 0));
            const totalIndustrialAccident = Math.round(totalCaregiverSalary * (props.taxData?.tr_san || 0));
            const monthlyInsurance = totalNationalPension + totalHealthInsurance + totalLongTermCare + totalEmploymentInsurance + totalUnemployment + totalIndustrialAccident;
            const annualInsurance = monthlyInsurance * 12;
            
            if (annualInsurance > 0) {
              generatedEntries.push({
                m_sisul_code: sisulCode,
                hb_year: year,
                hb_gubun: 'OUT',
                hb_scode: '1151',
                hb_bud_class: '',
                hb_memo: '요양보호사 사회보험부담금',
                hb_price: String(perPersonInsurance),
                hb_person: String(caregiverCount),
                hb_month: '12',
                hb_amount: String(annualInsurance),
                hb_funding: '06',
                pay_rel_group: payRelGroup,
                pay_rel_type: 'insurance',
              });
              
              budgetItems.push({
                code: `${q.code}_caregiver_insurance`,
                section: q.section,
                label: '요양보호사 사회보험부담금',
                monthly: monthlyInsurance,
                annual: annualInsurance
              });
            }
            
            // 일반 요양보호사 퇴직금 (1인당 기준)
            const perPersonRetirement = Math.round(monthlySalary / 12);
            // 정확한 총액 = 월 총 급여
            const annualRetirement = totalCaregiverSalary;
            
            generatedEntries.push({
              m_sisul_code: sisulCode,
              hb_year: year,
              hb_gubun: 'OUT',
              hb_scode: '1141',
              hb_bud_class: '',
              hb_memo: '요양보호사 퇴직금',
              hb_price: String(perPersonRetirement),
              hb_person: String(caregiverCount),
              hb_month: '12',
              hb_amount: String(annualRetirement),
              hb_funding: '06',
              pay_rel_group: payRelGroup,
              pay_rel_type: 'retirement',
            });
            
            budgetItems.push({
              code: `${q.code}_caregiver_retirement`,
              section: q.section,
              label: '요양보호사 퇴직금',
              monthly: Math.round(annualRetirement / 12),
              annual: annualRetirement
            });
          }
        } catch { /* ignore */ }
      }
      return;
    }
    
    // social-worker-salary 타입: 대표자/일반 구분하여 저장, 가산금 세입 추가
    if (q.inputType === 'social-worker-salary') {
      const savedAnswer = answers.value[q.code];
      if (savedAnswer && typeof savedAnswer === 'string') {
        try {
          const parsed = JSON.parse(savedAnswer);
          const hasCeo = parsed['hasCeo'];
          const ceoSalary = Number(parsed['ceoSalary']) || 0;
          const ceoCount = Number(parsed['ceoCount']) || 0;
          const workerCount = Number(parsed['workerCount']) || 0;
          const workerSalary = Number(parsed['workerSalary']) || 0;
          
          // 전체 사회복지사 수 (가산금 계산용)
          const totalWorkerCount = (hasCeo ? ceoCount : 0) + workerCount;
          
          // 대표자 저장 (고용/산재/퇴직금 제외)
          if (hasCeo && ceoSalary > 0 && ceoCount > 0) {
            const totalCeoSalary = ceoSalary * ceoCount;
            const annualCeoSalary = totalCeoSalary * 12;
            const payRelGroup = `salary_${q.code}_ceo`;
            
            // 대표자 급여
            generatedEntries.push({
              m_sisul_code: sisulCode,
              hb_year: year,
              hb_gubun: 'OUT',
              hb_scode: q.scode || '1111',
              hb_bud_class: '',
              hb_memo: '사회복지사(대표자) 급여',
              hb_price: String(ceoSalary),
              hb_person: String(ceoCount),
              hb_month: '12',
              hb_amount: String(annualCeoSalary),
              hb_funding: q.funding || '06',
              pay_rel_group: payRelGroup,
              pay_rel_type: 'salary',
            });
            
            budgetItems.push({
              code: `${q.code}_ceo`,
              section: q.section,
              label: '사회복지사(대표자) 급여',
              monthly: totalCeoSalary,
              annual: annualCeoSalary
            });
            
            // 대표자 사회보험부담금 (국민연금, 건강보험, 장기요양만) - 1인당 기준
            const perPersonNationalPension = Math.round(ceoSalary * (props.taxData?.tr_yeon || 0));
            const perPersonHealthInsurance = Math.round(ceoSalary * (props.taxData?.tr_gun || 0));
            const perPersonLongTermCare = Math.round(perPersonHealthInsurance * (props.taxData?.tr_jang || 0));
            const perPersonInsurance = perPersonNationalPension + perPersonHealthInsurance + perPersonLongTermCare;
            
            // 정확한 총액 (월 총 급여 기준)
            const totalNationalPension = Math.round(totalCeoSalary * (props.taxData?.tr_yeon || 0));
            const totalHealthInsurance = Math.round(totalCeoSalary * (props.taxData?.tr_gun || 0));
            const totalLongTermCare = Math.round(totalHealthInsurance * (props.taxData?.tr_jang || 0));
            const monthlyInsurance = totalNationalPension + totalHealthInsurance + totalLongTermCare;
            const annualInsurance = monthlyInsurance * 12;
            
            if (annualInsurance > 0) {
              generatedEntries.push({
                m_sisul_code: sisulCode,
                hb_year: year,
                hb_gubun: 'OUT',
                hb_scode: '1151',
                hb_bud_class: '',
                hb_memo: '사회복지사(대표자) 사회보험부담금',
                hb_price: String(perPersonInsurance),
                hb_person: String(ceoCount),
                hb_month: '12',
                hb_amount: String(annualInsurance),
                hb_funding: '06',
                pay_rel_group: payRelGroup,
                pay_rel_type: 'insurance',
              });
              
              budgetItems.push({
                code: `${q.code}_ceo_insurance`,
                section: q.section,
                label: '사회복지사(대표자) 사회보험부담금',
                monthly: monthlyInsurance,
                annual: annualInsurance
              });
            }
          }
          
          // 일반 사회복지사 저장 (모든 보험 + 퇴직금)
          if (workerCount > 0 && workerSalary > 0) {
            const totalWorkerSalary = workerSalary * workerCount;
            const annualWorkerSalary = totalWorkerSalary * 12;
            const payRelGroup = `salary_${q.code}_worker`;
            
            // 일반 사회복지사 급여
            generatedEntries.push({
              m_sisul_code: sisulCode,
              hb_year: year,
              hb_gubun: 'OUT',
              hb_scode: q.scode || '1111',
              hb_bud_class: '',
              hb_memo: '사회복지사 급여',
              hb_price: String(workerSalary),
              hb_person: String(workerCount),
              hb_month: '12',
              hb_amount: String(annualWorkerSalary),
              hb_funding: q.funding || '06',
              pay_rel_group: payRelGroup,
              pay_rel_type: 'salary',
            });
            
            budgetItems.push({
              code: `${q.code}_worker`,
              section: q.section,
              label: '사회복지사 급여',
              monthly: totalWorkerSalary,
              annual: annualWorkerSalary
            });
            
            // 일반 사회복지사 사회보험부담금 (1인당 기준)
            const perPersonNationalPension = Math.round(workerSalary * (props.taxData?.tr_yeon || 0));
            const perPersonHealthInsurance = Math.round(workerSalary * (props.taxData?.tr_gun || 0));
            const perPersonLongTermCare = Math.round(perPersonHealthInsurance * (props.taxData?.tr_jang || 0));
            const perPersonEmploymentInsurance = Math.round(workerSalary * (props.taxData?.tr_go || 0));
            const perPersonUnemployment = Math.round(workerSalary * (props.taxData?.tr_sil || 0));
            const perPersonIndustrialAccident = Math.round(workerSalary * (props.taxData?.tr_san || 0));
            const perPersonInsurance = perPersonNationalPension + perPersonHealthInsurance + perPersonLongTermCare + perPersonEmploymentInsurance + perPersonUnemployment + perPersonIndustrialAccident;
            
            // 정확한 총액 (월 총 급여 기준)
            const totalNationalPension = Math.round(totalWorkerSalary * (props.taxData?.tr_yeon || 0));
            const totalHealthInsurance = Math.round(totalWorkerSalary * (props.taxData?.tr_gun || 0));
            const totalLongTermCare = Math.round(totalHealthInsurance * (props.taxData?.tr_jang || 0));
            const totalEmploymentInsurance = Math.round(totalWorkerSalary * (props.taxData?.tr_go || 0));
            const totalUnemployment = Math.round(totalWorkerSalary * (props.taxData?.tr_sil || 0));
            const totalIndustrialAccident = Math.round(totalWorkerSalary * (props.taxData?.tr_san || 0));
            const monthlyInsurance = totalNationalPension + totalHealthInsurance + totalLongTermCare + totalEmploymentInsurance + totalUnemployment + totalIndustrialAccident;
            const annualInsurance = monthlyInsurance * 12;
            
            if (annualInsurance > 0) {
              generatedEntries.push({
                m_sisul_code: sisulCode,
                hb_year: year,
                hb_gubun: 'OUT',
                hb_scode: '1151',
                hb_bud_class: '',
                hb_memo: '사회복지사 사회보험부담금',
                hb_price: String(perPersonInsurance),
                hb_person: String(workerCount),
                hb_month: '12',
                hb_amount: String(annualInsurance),
                hb_funding: '06',
                pay_rel_group: payRelGroup,
                pay_rel_type: 'insurance',
              });
              
              budgetItems.push({
                code: `${q.code}_worker_insurance`,
                section: q.section,
                label: '사회복지사 사회보험부담금',
                monthly: monthlyInsurance,
                annual: annualInsurance
              });
            }
            
            // 일반 사회복지사 퇴직금 (1인당 기준)
            const perPersonRetirement = Math.round(workerSalary / 12);
            // 정확한 총액 = 월 총 급여
            const annualRetirement = totalWorkerSalary;
            
            generatedEntries.push({
              m_sisul_code: sisulCode,
              hb_year: year,
              hb_gubun: 'OUT',
              hb_scode: '1141',
              hb_bud_class: '',
              hb_memo: '사회복지사 퇴직금',
              hb_price: String(perPersonRetirement),
              hb_person: String(workerCount),
              hb_month: '12',
              hb_amount: String(annualRetirement),
              hb_funding: '06',
              pay_rel_group: payRelGroup,
              pay_rel_type: 'retirement',
            });
            
            budgetItems.push({
              code: `${q.code}_worker_retirement`,
              section: q.section,
              label: '사회복지사 퇴직금',
              monthly: Math.round(annualRetirement / 12),
              annual: annualRetirement
            });
          }
          
          // 가산금 세입 추가 (전체 사회복지사 수 기준)
          const targetClients = parseNumber(String(answers.value['revenue_target_clients'])) || 0;
          if (targetClients > 0 && suga && totalWorkerCount > 0) {
            const clientBillingAmount = sugaMoney * 20;
            const bonusScore = getSocialWorkerBonusPoint(totalWorkerCount);
            const monthlyBonus = Math.round((clientBillingAmount * 0.85 / targetClients) * bonusScore * 1.8 * targetClients);
            const annualBonus = monthlyBonus * 12;
            
            if (annualBonus > 0) {
              generatedEntries.push({
                m_sisul_code: sisulCode,
                hb_year: year,
                hb_gubun: 'IN',
                hb_scode: '613',
                hb_bud_class: '',
                hb_memo: '사회복지사 가산금',
                hb_price: String(monthlyBonus),
                hb_person: '1',
                hb_month: '12',
                hb_amount: String(annualBonus),
                hb_funding: '06',
              });
              
              budgetItems.push({
                code: `${q.code}_bonus`,
                section: '세입',
                label: '사회복지사 가산금',
                monthly: monthlyBonus,
                annual: annualBonus
              });
            }
          }
        } catch { /* ignore */ }
      }
      return;
    }
    
    const value = parseNumber(String(answers.value[q.code])) || 0;
    let monthly = 0;
    let annual = 0;
    
    // Legacy 처리 (제거 예정)
    if (q.code === 'expense_social_worker_salary_legacy') {
      const count = 2; // 기본 2명 (Q&A에서 제외됨)
      monthly = value * count;
      annual = monthly * 12;
    } else if (q.frequency === 'monthly') {
      monthly = value;
      annual = value * 12;
    } else if (q.frequency === 'once') {
      monthly = Math.round(value / 12);
      annual = value;
    } else if (q.frequency === 'count') {
      // 인원수는 계산에서 제외
      monthly = 0;
      annual = 0;
    }
    
    budgetItems.push({
      code: q.code,
      section: q.section,
      label: q.shortLabel || q.question,
      monthly,
      annual
    });
  });
  
  // 기본 운영비 항목 자동 추가 (값이 비어있어도 내용이 있어야 함)
  // 수용비 및 수수료 (scode: 132)
  const defaultExpense132Items = [
    '사무용품비',
    '집기류 구입',
    '각종 렌탈비',
    '기타 수용비'
  ];
  
  defaultExpense132Items.forEach(itemName => {
    generatedEntries.push({
      m_sisul_code: sisulCode,
      hb_year: year,
      hb_gubun: 'OUT',
      hb_scode: '132',
      hb_bud_class: '',
      hb_memo: itemName,
      hb_price: '',
      hb_person: '',
      hb_month: '',
      hb_amount: '',
      hb_funding: ''
    });
  });
  
  // 공공요금 및 각종 세금공과금 (scode: 133)
  const defaultExpense133Items = [
    '인터넷 및 전화요금',
    '전기요금',
    '각종 공공요금',
    '각종 보험금',
    '협회비'
  ];
  
  defaultExpense133Items.forEach(itemName => {
    generatedEntries.push({
      m_sisul_code: sisulCode,
      hb_year: year,
      hb_gubun: 'OUT',
      hb_scode: '133',
      hb_bud_class: '',
      hb_memo: itemName,
      hb_price: '',
      hb_person: '',
      hb_month: '',
      hb_amount: '',
      hb_funding: ''
    });
  });
  
  // 생성된 예산 엔트리 저장
  budgetEntries.value = generatedEntries;
  
  const budgetData: BudgetData = {
    year: searchParams.value.lebYear,
    answers: { ...answers.value },
    calculations: {
      totalRevenue: totalRevenue.value,
      totalExpense: totalExpense.value,
      balance: balance.value,
      bonusRevenue: bonusRevenue.value,
      items: budgetItems
    },
    budgetEntries: generatedEntries
  };
  
  emit('complete', budgetData);
};

watch(compositeAnswers, (newVal, oldVal) => {
  if (currentQuestion.value?.inputType === 'composite') {
    saveCurrentAnswer();
  }
  
  // director-salary: 대표자 스위치 해제 시 대표자 관련 값 초기화
  if (currentQuestion.value?.inputType === 'director-salary') {
    if (oldVal?.['hasCeo'] === true && newVal?.['hasCeo'] === false) {
      compositeAnswers.value['ceoSalary'] = '';
      compositeAnswers.value['ceoCount'] = 1;
    }
  }
  
  // social-worker-salary: 대표자 스위치 해제 시 대표자 관련 값 초기화
  if (currentQuestion.value?.inputType === 'social-worker-salary') {
    if (oldVal?.['hasCeo'] === true && newVal?.['hasCeo'] === false) {
      compositeAnswers.value['ceoSalary'] = '';
      compositeAnswers.value['ceoCount'] = 1;
    }
  }
  
  // caregiver-salary: 대표자 스위치 해제 시 대표자 관련 값 초기화
  if (currentQuestion.value?.inputType === 'caregiver-salary') {
    if (oldVal?.['hasCeo'] === true && newVal?.['hasCeo'] === false) {
      compositeAnswers.value['ceoHourlyWage'] = '';
      compositeAnswers.value['ceoCount'] = 1;
    }
  }
}, { deep: true });

// props.year가 변경되면 searchParams.lebYear 동기화
watch(() => props.year, (newYear) => {
  if (newYear && newYear !== searchParams.value.lebYear) {
    searchParams.value.lebYear = newYear;
  }
}, { immediate: true });

// props.sugaList가 변경되면 질문 옵션 업데이트
watch(() => props.sugaList, () => {
  updateSugaOptions();
}, { immediate: true });

onMounted(() => {
  if (totalQuestions.value > 0) {
    loadCurrentAnswer();
    focusFirstInput();
  }
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/pgmBudgetStarter.scss";
</style>

<!-- 툴팁은 body에 렌더링되므로 전역 스타일 필요 -->
<style lang="scss">
.labor-tooltip-content {
  padding: 4px 0;

  .tooltip-title {
    font-size: 14px;
    font-weight: 700;
    color: white;
    margin-bottom: 8px;
    padding-bottom: 6px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  }

  .tooltip-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 2px;

    .tooltip-label {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.85);
    }

    .tooltip-value {
      font-size: 13px;
      font-weight: 600;
      color: white;
    }
  }

  .tooltip-note {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 8px;
  }

  .tooltip-formula {
    font-size: 12px;
    color: #fcd34d;
    font-weight: 600;
    margin-top: 8px;
    padding-top: 6px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
  }
}
</style>
